/* eslint-disable @typescript-eslint/no-unsafe-return -- allow */
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from 'tailwind-ui'
import { Navigate, NavLink, type UIMatch } from 'react-router'
import { Suspense, useState, useMemo } from 'react'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import { lazyWithPreload as lazy } from '@/utils/lazy-preload'
import DashLayout from '@/pages/[org_id]/layout'
import RootLayout from '@/pages/layout'
import IndexRoute from '@/pages/index'
import {
  SectionVenueGatePage,
  VenueGatePage,
} from '@/components/nav-v4/venue-gate-page'
import { SchemeGatePage } from '@/components/nav-v4/scheme-picker-gate'
import LoyaltySchemePreviewPage from '@/pages/[org_id]/loyalty/[id]/preview'
import { DayPlannerSkeleton } from '@/pages/[org_id]/venues/[serial]/bookings/day-planner-fast/components/DayPlannerSkeleton'
import { ListViewSkeleton } from '@/pages/[org_id]/venues/[serial]/bookings/list-view-3/components/list-view-skeleton'
import { RedirectByTier } from '@/pages/[org_id]/venues/[serial]/wi-fi/redirect-by-tier'
import { MenusMovedToOrdering } from '@/pages/[org_id]/bookings/menus-moved-to-ordering'
import { PlacecardsMovedToOrdering } from '@/pages/[org_id]/bookings/placecards-moved-to-ordering'
import type { NormalRoutesType } from './types'
import { useMenu } from './pages/[org_id]/menu-hook'
import { useVenues } from './hooks/useVenue'
import { usePathname } from './helpers/router'

function VenueSwitchCrumb({ data }: { data: UIMatch }) {
  const [venues, findAlias] = useVenues()
  const pathname = usePathname()
  const [search, setSearch] = useState('')
  const filteredVenues = useMemo(() => {
    return venues.filter((venue: any) =>
      venue.alias?.toLowerCase().includes(search.toLowerCase())
    )
  }, [venues, search])
  if (venues.length <= 1) {
    return (
      <p className="flex items-center h-full gap-1 focus:outline-none truncate">
        {findAlias(data.params.serial)}
      </p>
    )
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center h-full gap-1 focus:outline-none ">
        <span className="truncate text-nowrap max-w-24">
          {findAlias(data.params.serial)}
        </span>

        <span className="sr-only">Toggle menu</span>
        <ChevronDownIcon className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-64 min-w-64 overflow-y-auto gap-0.5 border border-black/10 dark:border-white/10 bg-neutral-50/90 dark:bg-neutral-950/90 rounded-xl"
      >
        <Input
          value={search}
          onChange={(e: any) => {
            setSearch(e.target.value)
          }}
          placeholder="Search..."
          className="w-full px-3 bg-transparent"
          size="sm"
          variant="flushed"
        />
        {filteredVenues.map((item: any) => (
          <DropdownMenuItem
            key={item?.serial}
            className={cn('size-full   overflow-hidden', {
              'text-stampede-500': item?.serial === data.params.serial,
            })}
          >
            <NavLink
              className="size-full"
              to={`/${item?.organization_id}${pathname.replace(data.params.serial, item?.serial)}`}
            >
              {item?.alias || item?.serial}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function OrgCrumb({ data }: { data: UIMatch }) {
  const findPath = items.visible.find((item: any) => item?.to === data.pathname)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 focus:outline-none">
        {findPath?.name ?? 'Venue'}

        <span className="sr-only">Toggle menu</span>
        <ChevronDownIcon className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {items.venue.map((item: any) => (
          <DropdownMenuItem key={item?.to} asChild>
            <NavLink className="size-full" to={item?.to}>
              {item?.name}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function VenueCrumb({ data }: { data: UIMatch }) {
  const items = useMenu({})
  const findPath = items.venue.find((item: any) => item?.to === data.pathname)
  const [search, setSearch] = useState('')

  const filteredItems = useMemo(() => {
    return items.venue.filter((item: any) =>
      item?.name?.toLowerCase().includes(search.toLowerCase())
    )
  }, [items.venue, search])

  if (items.venue.length === 1) {
    return (
      <NavLink
        className="flex items-center gap-1 focus:outline-none"
        to={findPath?.to ?? ''}
      >
        {findPath?.name}
      </NavLink>
    )
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1 h-full focus:outline-none">
        {findPath?.name ?? 'Venue'}

        <span className="sr-only">Toggle menu</span>
        <ChevronDownIcon className="size-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="gap-0.5 border border-black/10 dark:border-white/10 bg-neutral-50/90 dark:bg-neutral-950/90 rounded-xl"
      >
        <Input
          value={search}
          onChange={(e: any) => {
            setSearch(e.target.value)
          }}
          placeholder="Search..."
          className="w-full px-3 bg-transparent"
          size="sm"
          variant="flushed"
        />
        {filteredItems.map((item: any) => (
          <DropdownMenuItem
            key={item?.to}
            asChild
            className={cn('size-full   overflow-hidden', {
              'text-stampede-500': item?.to === data.pathname,
            })}
          >
            <NavLink className="size-full" end to={item?.to}>
              {item?.name}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// Hoisted so the index (`/chat-3`) and `:thread_id` (`/chat-3/:thread_id`)
// entries below share ONE component reference. Two separate `lazy(() =>
// import(...))` calls — even pointing at the identical module — are two
// distinct component identities, so React remounts the page (wiping its
// local state, e.g. which agent the picker has selected) the moment a fresh
// conversation's first message attaches a thread id to the URL and the
// route match flips from the index entry to the `:thread_id` entry.
const chatThreePage = lazy(() => import('@/pages/[org_id]/chat-3/page'))
// Same hoisting rule as chatThreePage above — one shared component
// reference for both the index and :thread_id entries, or React remounts
// the page (and wipes its local state) the moment a fresh thread's first
// message attaches an id to the URL.
const knowledgeBasePage = lazy(
  () => import('@/pages/[org_id]/knowledge-base/page')
)

export const newRoutesArray: NormalRoutesType[] = [
  {
    path: '/',
    component: <RootLayout />,
    children: [
      {
        path: 'partner',
        component: lazy(() => import('@/pages/partner/layout')),
        children: [
          {
            index: true,
            component: lazy(() => import('@/pages/partner/page')),
          },
          {
            path: 'map',
            component: lazy(() => import('@/pages/partner/map')),
          },
          {
            path: 'venues',
            component: lazy(() => import('@/pages/partner/venues/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/partner/venues')),
              },
            ],
          },
          {
            path: 'organisations',
            component: lazy(
              () => import('@/pages/partner/organisations/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/partner/organisations')),
              },
            ],
          },
        ],
      },
      {
        index: true,
        component: <IndexRoute />,
      },
      {
        // Org-less knowledge-base deep link: resolves the user's organisation
        // and forwards to /<org>/venues?knowledge_base=true. Static segment,
        // so it wins over the `:org_id` param route.
        path: 'knowledge-base',
        component: lazy(() => import('@/pages/knowledge-base-shortcut')),
      },
      {
        // "Open in Stampede" links from the public help articles:
        // /open/<path> resolves the user's organisation and forwards to
        // /<org>/<path>. Static segment, so it wins over `:org_id`.
        path: 'open/*',
        component: lazy(() => import('@/pages/open-shortcut')),
      },
      {
        // Referral capture link: stores the referring org id in a cookie,
        // then forwards to '/'. Static prefix, so it wins over the
        // `:org_id` param route.
        path: 'r/:org_id',
        component: lazy(() => import('@/pages/referral')),
      },
      {
        path: 'pwa',
        component: lazy(() => import('@/pages/pwa/page')),
      },
      {
        path: 'marketing/senders',
        component: lazy(() => import('@/pages/marketing/senders')),
      },
      {
        path: 'marketing/email-verification',
        component: lazy(() => import('@/pages/marketing/senders')),
      },
      {
        path: 'code',
        component: lazy(() => import('@/pages/oauth/code')),
      },

      {
        path: 'oauth',
        component: lazy(() => import('@/pages/oauth/layout')),
        children: [
          {
            path: 'logout',
            component: lazy(() => import('@/pages/oauth/logout')),
          },
          {
            path: 'code',
            component: lazy(() => import('@/pages/oauth/code')),
          },
          {
            path: 'google/callback',
            component: lazy(() => import('@/pages/oauth/google-callback')),
          },
        ],
      },
      {
        path: 'netxl',
        component: lazy(() => import('@/pages/netxl/layout')),
        children: [
          {
            path: 'payment/success',
            component: lazy(() => import('@/pages/netxl/payment/success')),
          },
          {
            path: 'payment/cancel',
            component: lazy(() => import('@/pages/netxl/payment/cancel')),
          },
        ],
      },
      {
        path: 'admin',
        component: lazy(() => import('@/pages/admin/layout')),
        children: [
          {
            index: true,
            component: lazy(() => import('@/pages/admin')),
          },
          {
            path: 'support',
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/support/page')),
              },
              {
                // What is being watched, by whom: every film ranked by views,
                // watchers, completions, and recent activity by name. Staff only
                // end to end: this sits under /admin and the trunk endpoint
                // refuses non-staff.
                path: 'videos',
                component: lazy(() => import('@/pages/admin/support/videos')),
              },
              {
                // Publishing a film somebody recorded, as opposed to one the
                // render pipeline made. Staff only, like everything that
                // writes to the shared library.
                path: 'upload-video',
                component: lazy(
                  () => import('@/pages/admin/support/upload-video/page')
                ),
              },
              {
                // Making films and looking after them. Staff work, so it lives
                // here and nowhere in an organisation's own menu: the library
                // is global and the films are for everyone, the studio is not.
                path: 'create-videos',
                children: [
                  {
                    // The catalogue: every film, by product area.
                    index: true,
                    component: lazy(
                      () => import('@/pages/admin/support/create-videos/page')
                    ),
                  },
                  {
                    // Ask for a film. With ?again=<production id> it is the
                    // same form, seeded from that request and sent back as a
                    // remake of the film it made.
                    path: 'new',
                    component: lazy(
                      () =>
                        import('@/pages/admin/support/create-videos/new/page')
                    ),
                  },
                  {
                    // How to take the screenshots, for the team.
                    path: 'how-to',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/admin/support/create-videos/how-to/page'
                        )
                    ),
                  },
                  {
                    path: ':production_id',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/admin/support/create-videos/[production_id]/page'
                        )
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'dpo',
            component: lazy(() => import('@/pages/admin/dpo/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/dpo/page')),
              },
            ],
          },
          {
            path: 'knowledge-base',
            component: lazy(
              () => import('@/pages/admin/knowledge-base/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/knowledge-base')),
              },
              {
                path: ':guide_id',
                component: lazy(
                  () => import('@/pages/admin/knowledge-base/[guide_id]')
                ),
              },
              {
                path: ':guide_id/edit',
                component: lazy(
                  () => import('@/pages/admin/knowledge-base/[guide_id]/edit')
                ),
              },
            ],
          },
          {
            path: 'design-kit',
            component: lazy(() => import('@/pages/design/components')),
          },
          {
            path: 'google-reviews',
            component: lazy(
              () => import('@/pages/admin/google-reviews/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/google-reviews')),
              },
            ],
          },
          {
            path: 'review-notification-emails',
            component: lazy(
              () => import('@/pages/admin/notification-emails/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/notification-emails')
                ),
              },
            ],
          },
          {
            path: 'gifting',
            component: lazy(() => import('@/pages/admin/gifting')),
          },
          {
            path: 'wi-fi',
            component: lazy(() => import('@/pages/admin/wi-fi/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/wi-fi/page')),
              },
              {
                path: 'unifi',
                component: lazy(() => import('@/pages/admin/wi-fi/unifi')),
              },
              {
                path: 'log-center',
                component: lazy(() => import('@/pages/admin/wi-fi/log-center')),
              },
              {
                path: 'health',
                component: lazy(() => import('@/pages/admin/wi-fi/health')),
              },
              {
                path: 'snap',
                component: lazy(() => import('@/pages/admin/wi-fi/snap')),
              },
              {
                // Re-exposed (D24): the NetXL hardware-ordering UI, previously
                // built but unrouted.
                path: 'netxl',
                component: lazy(() =>
                  import(
                    '@/pages/admin/wi-fi/components/netxl/hardware-orders-content'
                  ).then((m) => ({ default: m.HardwareOrdersContent }))
                ),
              },
              {
                // Re-exposed (D24): the snap-provisioning list/wizard.
                path: 'snap-provisioning',
                component: lazy(() =>
                  import('@/pages/admin/wi-fi/snap-provisioning-list').then(
                    (m) => ({ default: m.SnapProvisioningList })
                  )
                ),
              },
              {
                path: 'vpn-servers',
                component: lazy(
                  () => import('@/pages/admin/wi-fi/vpn-servers/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import(
                          '@/pages/admin/wi-fi/vpn-servers/infrastructure-list'
                        )
                    ),
                  },
                  {
                    path: 'list',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/admin/wi-fi/vpn-servers/infrastructure-list'
                        )
                    ),
                  },
                  {
                    path: 'devices',
                    component: lazy(
                      () => import('@/pages/admin/wi-fi/vpn-servers/devices')
                    ),
                  },
                  {
                    path: 'network',
                    component: lazy(
                      () => import('@/pages/admin/wi-fi/vpn-servers/network')
                    ),
                  },
                  {
                    path: 'servers',
                    component: lazy(
                      () => import('@/pages/admin/wi-fi/vpn-servers/servers')
                    ),
                  },
                  {
                    path: 'map',
                    component: lazy(
                      () => import('@/pages/admin/wi-fi/vpn-servers/map')
                    ),
                  },
                  {
                    path: 'activity',
                    component: lazy(
                      () => import('@/pages/admin/wi-fi/vpn-servers/activity')
                    ),
                  },
                  {
                    path: ':serverId',
                    component: lazy(
                      () =>
                        import('@/pages/admin/wi-fi/vpn-servers/server-detail')
                    ),
                  },
                ],
              },
              {
                path: 'map',
                component: lazy(() => import('@/pages/admin/wi-fi/map')),
              },
            ],
          },
          {
            path: 'demo-bookings',
            component: lazy(() => import('@/pages/admin/demo-bookings')),
          },
          {
            path: 'topology',
            component: lazy(() => import('@/pages/admin/topology')),
          },
          {
            path: 'cache',
            component: lazy(() => import('@/pages/admin/cache/page')),
          },
          {
            path: 'venues',
            component: lazy(() => import('@/pages/admin/venues/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/venues')),
              },
              {
                path: 'wall',
                component: lazy(() => import('@/pages/admin/venues/wall')),
              },
            ],
          },
          {
            path: 'queue',
            component: lazy(() => import('@/pages/admin/queue/layout')),
            children: [
              {
                index: true,
                component: <Navigate to="failed" replace />,
              },
              {
                path: 'failed',
                component: lazy(() => import('@/pages/admin/queue/failed')),
              },
            ],
          },
          {
            path: 'simple-segments',
            component: lazy(
              () => import('@/pages/admin/simple-segments/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/simple-segments/index')
                ),
              },
            ],
          },
          {
            path: 'simple-segments-requests',
            component: lazy(
              () => import('@/pages/admin/simple-segments-requests/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/simple-segments-requests/index')
                ),
              },
            ],
          },
          {
            path: 'admin-users',
            component: lazy(() => import('@/pages/admin/admin-users/layout')),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/admin-users/index')
                ),
              },
            ],
          },
          {
            path: 'marketing',
            component: lazy(() => import('@/pages/admin/marketing/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/marketing')),
              },
              {
                path: 'email',
                component: lazy(() => import('@/pages/admin/marketing/email')),
              },
              {
                path: 'blacklist',
                component: lazy(
                  () => import('@/pages/admin/marketing/blacklist/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/admin/marketing/blacklist')
                    ),
                  },
                ],
              },
              {
                path: 'email-addresses',
                component: lazy(
                  () => import('@/pages/admin/marketing/email-addresses')
                ),
              },
              {
                path: 'domains',
                component: lazy(
                  () => import('@/pages/admin/marketing/domains')
                ),
              },
            ],
          },
          {
            path: 'interactions',
            component: lazy(() => import('@/pages/admin/interactions')),
          },
          {
            path: 'sessions',
            component: lazy(() => import('@/pages/admin/sessions')),
          },
          {
            path: 'search',
            component: lazy(() => import('@/pages/admin/search')),
          },
          {
            path: 'map',
            component: lazy(() => import('@/pages/admin/map')),
          },
          {
            path: 'reports',
            component: lazy(() => import('@/pages/admin/reports')),
          },

          {
            path: 'organisations',
            component: lazy(() => import('@/pages/admin/organisations/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/organisations')),
              },
              // D25: org detail opens in a Sheet at the list level via a
              // `?org=` deep-link. These legacy nested paths redirect into it.
              {
                path: ':org_id',
                component: lazy(
                  () => import('@/pages/admin/organisations/org-redirect')
                ),
              },
              {
                path: ':org_id/report',
                component: lazy(
                  () => import('@/pages/admin/organisations/org-redirect')
                ),
              },
              {
                path: ':org_id/billing',
                component: lazy(
                  () => import('@/pages/admin/organisations/org-redirect')
                ),
              },
            ],
          },
          {
            path: 'customers',
            component: lazy(() => import('@/pages/admin/customers/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/customers')),
              },
              {
                path: ':id',
                component: lazy(() => import('@/pages/admin/customers/[id]')),
              },
            ],
          },
          {
            path: 'contacts',
            component: lazy(() => import('@/pages/admin/contacts/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/contacts')),
              },
            ],
          },
          {
            path: 'account-health',
            component: lazy(
              () => import('@/pages/admin/account-health/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/account-health')),
              },
              {
                path: 'board',
                component: lazy(
                  () => import('@/pages/admin/account-health/board')
                ),
              },
            ],
          },
          {
            path: 'auth-clients',
            component: lazy(() => import('@/pages/admin/auth-clients/layout')),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/auth-clients/index')
                ),
              },
            ],
          },
          {
            path: 'auth-token-logs',
            component: lazy(
              () => import('@/pages/admin/auth-token-logs/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/admin/auth-token-logs/index')
                ),
              },
            ],
          },
          {
            path: 'revenue',
            component: lazy(() => import('@/pages/admin/revenue/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/revenue')),
              },
              {
                path: 'subscriptions',
                component: lazy(
                  () => import('@/pages/admin/revenue/subscriptions')
                ),
              },
              {
                path: 'invoices',
                component: lazy(() => import('@/pages/admin/revenue/invoices')),
              },
              {
                path: 'products',
                component: lazy(() => import('@/pages/admin/revenue/products')),
              },
              {
                path: 'feature-value',
                component: lazy(
                  () => import('@/pages/admin/revenue/feature-value')
                ),
              },
              {
                path: 'organisations',
                component: lazy(
                  () => import('@/pages/admin/revenue/organisations')
                ),
              },
            ],
          },
          {
            path: 'suppression',
            component: lazy(() => import('@/pages/admin/suppression/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/admin/suppression')),
              },
            ],
          },
          {
            path: 'onboarding',
            component: lazy(() => import('@/pages/admin/onboarding')),
          },
          {
            path: 'onboarding/:id',
            component: lazy(() => import('@/pages/admin/onboarding/[id]')),
          },
          {
            path: 'onboarding-copilot',
            component: lazy(
              () => import('@/pages/admin/onboarding-copilot/page')
            ),
          },
        ],
      },
      {
        path: 'status',
        component: lazy(() => import('@/pages/status/layout')),
        children: [
          {
            path: 'vpn',
            component: lazy(() => import('@/pages/status/vpn')),
          },
        ],
      },
      {
        path: ':org_id',
        component: <DashLayout />,

        children: [
          {
            index: true,
            component: <Navigate to="venues" replace />,
          },
          {
            path: 'home-2025',
            component: lazy(() => import('@/pages/[org_id]/home-2025/page')),
          },
          {
            path: 'home-map',
            component: lazy(() => import('@/pages/[org_id]/home-map')),
          },
          {
            path: 'scanner',
            component: lazy(() => import('@/pages/[org_id]/scanner')),
          },
          {
            path: 'referrals',
            component: lazy(() => import('@/pages/[org_id]/referrals')),
          },
          {
            // Ungated: it is this organisation's own setup list, and the
            // people most likely to need it are the ones who have just
            // arrived and hold the fewest permissions. The endpoint is
            // org-scoped, so there is nothing here to protect from a
            // member of the organisation it describes.
            path: 'onboarding',
            component: lazy(() => import('@/pages/[org_id]/onboarding')),
          },
          {
            // Ungated for the same reason as the setup list it hangs
            // off: booking your own onboarding call is not a
            // permissioned activity.
            path: 'onboarding/calls',
            component: lazy(() => import('@/pages/[org_id]/onboarding/calls')),
          },
          {
            // The same list, walked one step at a time. Ungated with
            // the rest of onboarding.
            path: 'onboarding/start',
            component: lazy(() => import('@/pages/[org_id]/onboarding/start')),
          },
          {
            // Ungated, like the upsell write-ups it links to: reading about
            // what the platform can do is not a permissioned activity.
            path: 'whats-new',
            component: lazy(() => import('@/pages/[org_id]/whats-new/index')),
          },
          {
            path: 'hardware',
            component: lazy(() => import('@/pages/[org_id]/hardware/index')),
          },
          {
            path: 'hardware/activate/:device_serial',
            component: lazy(
              () =>
                import(
                  '@/pages/[org_id]/hardware/activate/[device_serial]/index'
                )
            ),
          },
          {
            path: 'square',
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/square')),
              },
              {
                path: 'table-mappings',
                component: lazy(
                  () => import('@/pages/[org_id]/square/mappings')
                ),
              },
              {
                // Retired: Square > Table Ordering mounted the Zion catalogue
                // editor. Menus (and whether they take table orders) live in
                // Ordering (Mouse) now. See `MenusMovedToOrdering`.
                path: 'ordering/*',
                component: <MenusMovedToOrdering />,
              },
              {
                path: 'discounts',
                component: lazy(
                  () => import('@/pages/[org_id]/square/discounts')
                ),
              },
              {
                path: 'loyalty',
                component: lazy(
                  () => import('@/pages/[org_id]/square/loyalty')
                ),
              },
              {
                path: 'customer-groups',
                component: lazy(
                  () => import('@/pages/[org_id]/square/customer-groups')
                ),
              },
              {
                path: 'traces',
                component: lazy(() => import('@/pages/[org_id]/square/traces')),
              },
              {
                // "What is this?" sales/info explainer — reachable from the
                // bottom of the Square POS sub-nav once an account is linked.
                path: 'what-is-this',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/marketplace/apps/square/what-is-this'
                    )
                ),
              },
            ],
          },
          {
            // Clean org-level brand kits path (reuses the account brand-kit
            // screens) so the nav lands on /:orgId/brandkits instead of the
            // deep /account/organisation/brand/:id URL.
            //
            // Guarded separately BECAUSE it reuses those screens from outside the
            // `account/organisation` subtree — the guard there does not cover
            // this path, so without this a role denied Brand Kits in the nav
            // could still reach the editor by URL.
            path: 'brandkits',
            capability: 'nav.brand',
            component: lazy(
              () => import('@/pages/[org_id]/account/organisation/brand/layout')
            ),
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/[org_id]/account/organisation/brand')
                ),
              },
              {
                path: ':id',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/account/organisation/brand/[id]/layout'
                    )
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/account/organisation/brand/[id]'
                        )
                    ),
                  },
                  {
                    path: 'journey',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/account/organisation/brand/[id]/journey'
                        )
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'chat',
            // Staff plus the agent-mode allow-list. Previously the nav simply
            // omitted the link, so the URL itself was reachable by anyone.
            capability: 'chat.agent-mode',
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/chat')),
              },
              {
                path: ':thread_id',
                component: lazy(() => import('@/pages/[org_id]/chat')),
              },
            ],
          },
          {
            path: 'chat-3',
            capability: 'chat.agent-mode',
            children: [
              {
                index: true,
                component: chatThreePage,
              },
              {
                path: ':thread_id',
                component: chatThreePage,
              },
            ],
          },
          {
            // The real Knowledge Base destination (knowledge-base-agent-
            // ard.md D4) — deliberately NO `capability` gate: PRD §8 is
            // every logged-in org user, no restriction, unlike chat-3's
            // staff-only test surface above.
            path: 'knowledge-base',
            children: [
              {
                index: true,
                component: knowledgeBasePage,
              },
              {
                // Knowledge Base videos: short narrated how-tos streamed from
                // assets.stampede.ai/kb-videos. Ungated, like the page above.
                // Declared before `:thread_id` so the static segment wins.
                path: 'videos',
                component: lazy(
                  () => import('@/pages/[org_id]/knowledge-base/videos/index')
                ),
              },
              {
                path: ':thread_id',
                component: knowledgeBasePage,
              },
            ],
          },
          {
            // Support: the customer support agent surface. `nav.support` is
            // open to everyone; it gates the nav entry, this route and the dock,
            // all reading the one capability so they cannot drift apart.
            path: 'support',
            capability: 'nav.support',
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/support/page')),
              },
              {
                path: 'cases',
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/support/cases/page')
                    ),
                  },
                  {
                    path: ':case_id',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/support/cases/[case_id]/page')
                    ),
                  },
                ],
              },
              {
                // The knowledge base film library, served by trunk from the
                // kb-videos prefix of the assets bucket. Declared before any
                // dynamic sibling so the static segment wins the match.
                path: 'videos',
                children: [
                  {
                    // The home: search, one recommendation row, the library.
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/support/videos/page')
                    ),
                  },
                  {
                    // The watch page. `:video_id` is the film's slug as the
                    // manifest names it (`connect-stripe`), not a cuid; trunk
                    // resolves either and answers with the canonical slug.
                    path: ':video_id',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/support/videos/[video_id]/page'
                        )
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'engineering',
            capability: 'internal.engineering',
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/[org_id]/engineering/page')
                ),
              },
              {
                path: ':thread_id',
                component: lazy(
                  () => import('@/pages/[org_id]/engineering/page')
                ),
              },
            ],
          },
          {
            path: 'bookings',
            children: [
              {
                index: true,
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/index-route')
                ),
              },
              {
                // Enquiries workspace (zion ENQUIRIES_WORKSPACE_ARD.md D17). Staff
                // plus the named early-access users listed in the policy while it
                // is built; the capability on this container guards the whole
                // subtree. Widen its rule to 'bookings.enquiries' to release it.
                path: 'enquiries-workspace',
                capability: 'bookings.enquiries-workspace',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/bookings/enquiries-workspace/layout'
                    )
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/bookings/enquiries-workspace')
                    ),
                  },
                  {
                    path: 'board',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/board'
                        )
                    ),
                  },
                  {
                    path: 'today',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/today'
                        )
                    ),
                  },
                  {
                    path: 'reports',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/reports'
                        )
                    ),
                  },
                  {
                    path: 'list',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/list'
                        )
                    ),
                  },
                  {
                    path: 'calendar',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/calendar'
                        )
                    ),
                  },
                  {
                    path: 'scheduled',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/scheduled'
                        )
                    ),
                  },
                  {
                    path: 'workflows',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/workflows'
                        )
                    ),
                  },
                  {
                    path: 'run-sheet',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/run-sheet'
                        )
                    ),
                  },
                  {
                    path: ':id/function-sheet',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/[id]/function-sheet'
                        )
                    ),
                  },
                  {
                    path: ':id/run-sheet',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/[id]/run-sheet'
                        )
                    ),
                  },
                  {
                    path: 'day-plan',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/day-plan'
                        )
                    ),
                  },
                  {
                    path: 'money',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/money'
                        )
                    ),
                  },
                  {
                    path: 'files',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/files'
                        )
                    ),
                  },
                  {
                    path: 'coordinator',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/coordinator'
                        )
                    ),
                  },
                  {
                    path: 'stages',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/stages'
                        )
                    ),
                  },
                  {
                    path: ':id',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/enquiries-workspace/[id]'
                        )
                    ),
                  },
                ],
              },
              {
                path: 'enquiries',
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/bookings/enquiries')
                    ),
                  },
                  {
                    path: 'reports',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/bookings/enquiries/reports')
                    ),
                  },
                  {
                    path: ':id',
                    component: lazy(
                      () => import('@/pages/[org_id]/bookings/enquiries/[id]')
                    ),
                  },
                ],
              },
              {
                path: 'deposits',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/deposits')
                ),
              },
              {
                // Moved to Ordering; staff print these every service and have
                // the old path bookmarked.
                path: 'placecards',
                component: <PlacecardsMovedToOrdering />,
              },
              {
                path: 'reports',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/reports')
                ),
              },
              {
                path: 'boost',
                // The page itself used to `return null` for non-staff, which
                // renders a blank screen rather than an explanation.
                capability: 'internal.boost',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/boost')
                ),
              },
              {
                path: 'marketing-reports',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/marketing-reports')
                ),
              },
              // Venue-only pages mirrored at org level — render the
              // "Choose a venue to continue" picker (Square pattern).
              // Generic gate for the Bookings settings sub-nav (driven by
              // `?next=`/`?label=`) so those items can stay visible at org level.
              {
                path: 'choose-venue',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/choose-venue')
                ),
              },
              {
                // Smart Seating at org scope: venue picker gate (the page is
                // venue-only; see venues/[serial]/bookings/optimisation).
                path: 'optimisation',
                component: lazy(
                  () => import('@/pages/[org_id]/bookings/optimisation')
                ),
              },
              {
                path: 'day-planner-fast',
                component: (
                  <VenueGatePage
                    label="Day planner"
                    venuePath="bookings/day-planner-fast"
                  />
                ),
              },
              {
                path: 'design',
                component: (
                  <VenueGatePage
                    label="Landing page"
                    venuePath="bookings/design"
                  />
                ),
              },
              {
                path: 'design/branding',
                component: (
                  <VenueGatePage
                    label="Landing page branding"
                    venuePath="bookings/design/branding"
                  />
                ),
              },
              {
                path: 'day-planner-3',
                component: (
                  <VenueGatePage
                    label="Day planner"
                    venuePath="bookings/day-planner-3"
                  />
                ),
              },
              {
                path: 'list-view-fast',
                component: (
                  <VenueGatePage
                    label="List view"
                    venuePath="bookings/list-view-fast"
                  />
                ),
              },
              {
                path: 'list-view-3',
                component: (
                  <VenueGatePage
                    label="List view"
                    venuePath="bookings/list-view-3"
                  />
                ),
              },
              {
                path: 'voice',
                capability: 'bookings.voice',
                component: (
                  <VenueGatePage label="Voice" venuePath="bookings/voice" />
                ),
              },
              {
                path: 'voice/calls',
                capability: 'bookings.voice',
                component: (
                  <VenueGatePage
                    label="Voice"
                    venuePath="bookings/voice/calls"
                  />
                ),
              },
              {
                path: 'voice/reporting',
                capability: 'bookings.voice',
                component: (
                  <VenueGatePage
                    label="Voice"
                    venuePath="bookings/voice/reporting"
                  />
                ),
              },
              {
                // The org-level door to Smart Seating. The page itself is
                // venue-scoped and always will be (the replay reads ONE
                // venue's book), but "What's new" and the sidebar both address
                // features at org level, so without this the announcement had
                // nowhere to send anyone. Same gate every other venue-only
                // surface uses rather than guessing a serial.
                path: 'optimisation',
                component: (
                  <VenueGatePage
                    label="Smart Seating"
                    venuePath="bookings/optimisation"
                  />
                ),
              },
              {
                // coming-soon pages for features still in the Stampede-only
                // pilot: what each does in detail, plus a route to sales.
                // Deliberately ungated - this is what non-staff users see.
                path: 'upsell',
                children: [
                  {
                    path: 'voice',
                    component: lazy(
                      () => import('@/pages/[org_id]/bookings/upsell/voice')
                    ),
                  },
                  {
                    path: 'wallets',
                    component: lazy(
                      () => import('@/pages/[org_id]/bookings/upsell/wallets')
                    ),
                  },
                  {
                    path: 'ticketed-events',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/bookings/upsell/ticketed-events'
                        )
                    ),
                  },
                ],
              },
              {
                path: 'floorplan-fast',
                component: (
                  <VenueGatePage
                    label="Floorplan"
                    venuePath="bookings/floorplan-fast"
                  />
                ),
              },
              {
                path: 'floorplan-3',
                component: (
                  <VenueGatePage
                    label="Floorplan"
                    venuePath="bookings/floorplan-3"
                  />
                ),
              },
              {
                path: 'floor-plans-edit',
                component: (
                  <VenueGatePage
                    label="Floorplan Editor"
                    venuePath="bookings/floor-plans-edit"
                  />
                ),
              },
              {
                // Retired: the catalogue lives in Ordering (Mouse) now. See
                // `MenusMovedToOrdering`.
                path: 'menus/*',
                component: <MenusMovedToOrdering />,
              },
              {
                path: 'outstanding-payments',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/outstanding-payments'
                    )
                ),
              },
              {
                path: 'booking-alerts',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/booking-alerts'
                    )
                ),
              },
              {
                path: 'csv-imports',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/csv-imports'
                    )
                ),
              },
              {
                path: 'csv-exports',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/csv-exports'
                    )
                ),
              },
              {
                path: 'csv-imports/new',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/csv-imports/new'
                    )
                ),
              },
              {
                path: 'csv-imports/:id',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/csv-imports/[id]'
                    )
                ),
              },
              {
                path: 'live-migration',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/venues/[serial]/bookings/live-migration'
                    )
                ),
              },
              {
                path: 'settings',
                children: [
                  {
                    path: 'areas',
                    component: (
                      <VenueGatePage
                        label="Areas"
                        venuePath="bookings/settings/areas"
                      />
                    ),
                  },
                  {
                    path: 'tables',
                    component: (
                      <VenueGatePage
                        label="Tables"
                        venuePath="bookings/settings/tables"
                      />
                    ),
                  },
                  {
                    path: 'combinations',
                    component: (
                      <VenueGatePage
                        label="Combinations"
                        venuePath="bookings/settings/combinations"
                      />
                    ),
                  },
                  {
                    path: 'archived/areas',
                    component: (
                      <VenueGatePage
                        label="Archived areas"
                        venuePath="bookings/settings/tables/areas/archived"
                      />
                    ),
                  },
                  {
                    path: 'archived/tables',
                    component: (
                      <VenueGatePage
                        label="Archived tables"
                        venuePath="bookings/settings/tables/archived"
                      />
                    ),
                  },
                  {
                    path: 'archived/combinations',
                    component: (
                      <VenueGatePage
                        label="Archived combinations"
                        venuePath="bookings/settings/tables/combinations/archived"
                      />
                    ),
                  },
                  {
                    path: 'archived/booking-types',
                    component: (
                      <VenueGatePage
                        label="Archived booking types"
                        venuePath="bookings/settings/booking-types/archived"
                      />
                    ),
                  },
                  {
                    // Org-level Main Settings: the sub-nav links carry a
                    // section segment (…/main-configuration/basic etc.), so
                    // the venue gate must catch those too — and forward the
                    // picked section, not just the root.
                    path: 'main-configuration',
                    children: [
                      {
                        index: true,
                        component: (
                          <VenueGatePage
                            label="Main settings"
                            venuePath="bookings/settings/main-configuration"
                          />
                        ),
                      },
                      {
                        path: ':sectionId',
                        component: (
                          <SectionVenueGatePage
                            label="Main settings"
                            venueBasePath="bookings/settings/main-configuration"
                          />
                        ),
                      },
                    ],
                  },
                  {
                    path: 'booking-types',
                    component: (
                      <VenueGatePage
                        label="Booking types"
                        venuePath="bookings/settings/booking-types"
                      />
                    ),
                  },
                  {
                    path: 'special-dates',
                    component: (
                      <VenueGatePage
                        label="Special dates"
                        venuePath="bookings/settings/special-dates"
                      />
                    ),
                  },
                  {
                    path: 'enquiries',
                    component: (
                      <VenueGatePage
                        label="Enquiry settings"
                        venuePath="bookings/settings/enquiries"
                      />
                    ),
                  },
                  {
                    path: 'events',
                    component: (
                      <VenueGatePage
                        label="Events"
                        venuePath="bookings/settings/events"
                      />
                    ),
                  },
                  {
                    path: 'ticketed-events',
                    component: (
                      <VenueGatePage
                        label="Ticketed events"
                        venuePath="bookings/settings/ticketed-events"
                      />
                    ),
                  },
                  {
                    path: 'upgrades',
                    component: (
                      <VenueGatePage
                        label="Upgrades"
                        venuePath="bookings/settings/upgrades"
                      />
                    ),
                  },
                  {
                    path: 'payment-reminders',
                    component: (
                      <VenueGatePage
                        label="Payment reminders"
                        venuePath="bookings/settings/payment-reminders"
                      />
                    ),
                  },
                  {
                    path: 'wallet',
                    component: (
                      <VenueGatePage
                        label="Wallet"
                        venuePath="bookings/settings/wallet"
                      />
                    ),
                  },
                  // Org-level doors to three venue-only pages, so a link from
                  // outside the product (a Knowledge Base article's "Open in
                  // Stampede") can be a plain path. The choose-venue form
                  // carries its target in the query string, which the login
                  // round-trip drops.
                  {
                    path: 'google',
                    component: (
                      <VenueGatePage
                        label="Google Integration"
                        venuePath="bookings/settings/google"
                      />
                    ),
                  },
                  {
                    path: 'link-builder',
                    component: (
                      <VenueGatePage
                        label="Link Builder"
                        venuePath="bookings/settings/embed-links"
                      />
                    ),
                  },
                  {
                    path: 'tracking',
                    component: (
                      <VenueGatePage
                        label="Tracking"
                        venuePath="bookings/settings/tracking"
                      />
                    ),
                  },
                  {
                    // Every Wallets child is a venue surface, so the org level
                    // is a "choose a venue" stop that forwards to the same path
                    // under the serial, exactly as `wallet` above does.
                    path: 'wallet/passes',
                    component: (
                      <VenueGatePage
                        label="Wallet passes"
                        venuePath="bookings/settings/wallet/passes"
                      />
                    ),
                  },
                  {
                    path: 'wallet/downloads',
                    component: (
                      <VenueGatePage
                        label="Wallet downloads"
                        venuePath="bookings/settings/wallet/downloads"
                      />
                    ),
                  },
                  {
                    path: 'email-templates',
                    component: (
                      <VenueGatePage
                        label="Email Templates"
                        venuePath="bookings/settings/email-templates"
                      />
                    ),
                  },
                ],
              },
            ],
          },
          {
            // Ordering (Mouse): org-level section. `nav.ordering` is the role
            // half of the gate; the layout asks Mouse whether the org has
            // ordering and shows an explanation otherwise. Menus are an org
            // library and orders span venues, so this lives beside Bookings,
            // not under a venue; the venue-scoped twin below presets the venue.
            path: 'ordering',
            capability: 'nav.ordering',
            component: lazy(() => import('@/pages/[org_id]/ordering/layout')),
            children: [
              {
                index: true,
                // Pre-ordering, not the mixed list: see `nav-v4/config.ts`.
                component: <Navigate to="orders/pre-ordering" replace />,
              },
              {
                path: 'orders',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/orders')
                ),
              },
              // The two channel-scoped views. Static segments, declared
              // BEFORE `orders/:orderId` so neither is ever read as an id.
              {
                path: 'orders/pre-ordering',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/orders')
                ),
              },
              {
                path: 'orders/table-ordering',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/orders')
                ),
              },
              {
                path: 'orders/:orderId',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/orders')
                ),
              },
              {
                path: 'menus',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/menus')
                ),
              },
              {
                path: 'menus/:menuId',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/menus/[menuId]')
                ),
              },
              {
                path: 'menus/:menuId/report',
                component: lazy(
                  () =>
                    import('@/pages/[org_id]/ordering/menus/[menuId]/report')
                ),
              },
              {
                path: 'menus/:menuId/replay',
                component: lazy(
                  () =>
                    import('@/pages/[org_id]/ordering/menus/[menuId]/replay')
                ),
              },
              {
                path: 'menus/:menuId/ai',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/menus/[menuId]/ai')
                ),
              },
              {
                path: 'venue-space/:serial',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/venue-space')
                ),
              },
              {
                path: 'products',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/products')
                ),
              },
              {
                path: 'modifiers',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/modifiers')
                ),
              },
              {
                /**
                 * TABLES AND QR BELONGS TO A BUILDING.
                 *
                 * An ordering point is keyed by serial, its zones are that
                 * venue's words, its floor plan is that venue's room and
                 * its QR sheet is what gets printed and stuck on those
                 * tables. The org view listed every venue's points in one
                 * stream, which is nobody's job, and the two controls that
                 * matter here (Floor plan, Add point) both had to ask for a
                 * venue anyway: one by sitting disabled, the other with a
                 * dropdown inside its dialog.
                 *
                 * So it asks first, the same way the day planner and the
                 * landing page do. Picking goes to the venue mount below,
                 * where the page already has its serial.
                 */
                path: 'tables',
                component: (
                  <VenueGatePage
                    label="Tables and QR"
                    venuePath="ordering/tables"
                  />
                ),
              },
              {
                // NOT gated: the sheet prints every venue's codes when it
                // has no serial, on purpose, and that is a thing somebody
                // wants once.
                path: 'tables/print',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/tables/print')
                ),
              },
              {
                // Moved here from Bookings: the cards describe what a guest
                // ORDERED. Its own page rather than the venue one below,
                // because `PlacecardsContent` takes an optional serial and
                // the org view is the every-venue one.
                path: 'placecards',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/placecards')
                ),
              },
              {
                // Every text the ordering service sent, and what became of
                // it. Org level is every venue; the venue twin below is the
                // same page reading the serial off the scope.
                path: 'sms',
                component: lazy(() => import('@/pages/[org_id]/ordering/sms')),
              },
              {
                // STAMPEDE ONLY. What guests said about the ordering APP,
                // which is not a review of the venue and must never reach
                // one. The capability is enforced at the router, so a typed
                // URL is refused and not merely unlinked.
                path: 'app-feedback',
                capability: 'internal.staff',
                component: lazy(
                  () => import('@/pages/[org_id]/ordering/app-feedback')
                ),
              },
            ],
          },
          {
            path: 'wi-fi',
            component: lazy(() => import('@/pages/[org_id]/wi-fi/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/wi-fi/page')),
              },
              // Venue-only Wi-Fi surfaces mirrored at org level — render the
              // "choose a venue" gate when no venue is selected.
              {
                path: 'activity',
                component: (
                  <VenueGatePage label="Activity" venuePath="wi-fi/activity" />
                ),
              },
              {
                path: 'sessions',
                component: (
                  <VenueGatePage label="Sessions" venuePath="wi-fi/sessions" />
                ),
              },
              {
                path: 'topology',
                component: (
                  <VenueGatePage
                    label="Topology"
                    venuePath="wi-fi/status/network-topology"
                  />
                ),
              },
              {
                path: 'access-points',
                component: (
                  <VenueGatePage
                    label="Access points"
                    venuePath="wi-fi/access-points"
                  />
                ),
              },
              {
                path: 'snap-guard',
                component: (
                  <VenueGatePage
                    label="SNAPguard"
                    venuePath="wi-fi/snap-guard"
                  />
                ),
              },
              {
                path: 'snap-guard/health',
                component: (
                  <VenueGatePage
                    label="SNAPguard health"
                    venuePath="wi-fi/snap-guard/health"
                  />
                ),
              },
              {
                path: 'snap-guard/setup',
                component: (
                  <VenueGatePage
                    label="SNAPguard setup"
                    venuePath="wi-fi/snap-guard/setup"
                  />
                ),
              },
              ...(
                [
                  [
                    'snap-guard/access-points',
                    'SNAPguard devices',
                    'wi-fi/snap-guard/access-points',
                  ],
                  [
                    'snap-guard/networks',
                    'SNAPguard networks',
                    'wi-fi/snap-guard/networks',
                  ],
                  [
                    'snap-guard/rate-limits',
                    'SNAPguard speed limits',
                    'wi-fi/snap-guard/rate-limits',
                  ],
                  [
                    'snap-guard/firewall',
                    'SNAPguard firewall',
                    'wi-fi/snap-guard/firewall',
                  ],
                  [
                    'snap-guard/console',
                    'SNAPguard console',
                    'wi-fi/snap-guard/console',
                  ],
                  ['payments', 'Portal payments', 'wi-fi/payments'],
                  ['ip-addresses', 'IP addresses', 'wi-fi/ip-addresses'],
                  ['firewall', 'Firewall', 'wi-fi/firewall'],
                  ['hardware/setup', 'Hardware setup', 'wi-fi/hardware/setup'],
                  [
                    'hardware/connections',
                    'Connections',
                    'wi-fi/hardware/connections',
                  ],
                ] as const
              ).map(([path, label, venuePath]) => ({
                path,
                component: (
                  <VenueGatePage label={label} venuePath={venuePath} />
                ),
              })),
              {
                path: 'branding',
                component: (
                  <VenueGatePage
                    label="Wi-Fi branding"
                    venuePath="brand/wi-fi"
                  />
                ),
              },
              {
                path: 'insight',
                component: (
                  <VenueGatePage label="Insight" venuePath="wi-fi/insight" />
                ),
              },
              {
                path: 'settings',
                component: (
                  <VenueGatePage label="Settings" venuePath="wi-fi/settings" />
                ),
              },
              {
                path: 'hardware',
                component: (
                  <VenueGatePage label="Hardware" venuePath="wi-fi/hardware" />
                ),
              },
              {
                path: 'snap',
                component: lazy(() => import('@/pages/[org_id]/wi-fi/snap')),
              },
              {
                path: 'unifi',
                component: lazy(
                  () => import('@/pages/[org_id]/marketplace/apps/unifi')
                ),
              },
              {
                path: 'unifi/:id',
                component: lazy(
                  () => import('@/pages/[org_id]/wi-fi/unifi/[id]/page')
                ),
              },
              {
                path: 'designs',
                component: lazy(() => import('@/pages/[org_id]/wi-fi/designs')),
              },
              {
                // Venue gate for venue-only Wi-Fi pages (Surveys / Upsell)
                // reached with no venue selected.
                path: 'choose-venue',
                component: lazy(
                  () => import('@/pages/[org_id]/wi-fi/choose-venue')
                ),
              },
              {
                path: 'surveys',
                component: lazy(() => import('@/pages/[org_id]/wi-fi/surveys')),
              },
              {
                path: 'survey',
                component: lazy(() => import('@/pages/[org_id]/wi-fi/surveys')),
              },
              {
                path: 'upsell',
                component: lazy(() => import('@/pages/[org_id]/wi-fi/upsell')),
              },
              {
                path: 'infrastructure',
                component: lazy(
                  () => import('@/pages/[org_id]/wi-fi/infrastructure/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/status')
                    ),
                  },
                  {
                    path: 'status',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/status')
                    ),
                  },
                  {
                    path: 'status/:serial',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/status')
                    ),
                  },
                  {
                    path: 'snap',
                    component: lazy(
                      () => import('@/pages/[org_id]/wi-fi/infrastructure/snap')
                    ),
                  },
                  {
                    path: 'snap/:serial',
                    component: lazy(
                      () => import('@/pages/[org_id]/wi-fi/infrastructure/snap')
                    ),
                  },
                  {
                    path: 'events',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/events')
                    ),
                  },
                  {
                    path: 'events/:serial',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/events')
                    ),
                  },
                  {
                    path: 'performance',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/wi-fi/infrastructure/performance'
                        )
                    ),
                  },
                  {
                    path: 'performance/:serial',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/wi-fi/infrastructure/performance'
                        )
                    ),
                  },
                  {
                    path: 'settings',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/settings')
                    ),
                  },
                  {
                    path: 'settings/:serial',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/settings')
                    ),
                  },
                  {
                    path: 'hardware',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/hardware')
                    ),
                  },
                  {
                    path: 'hardware/:serial',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/wi-fi/infrastructure/hardware')
                    ),
                  },
                ],
              },
            ],
          },
          {
            path: 'test',
            component: lazy(() => import('@/pages/[org_id]/test/layout')),
            children: [
              {
                component: lazy(() => import('@/pages/[org_id]/test')),
                index: true,
              },
              {
                component: lazy(() => import('@/pages/[org_id]/test/table')),
                path: 'table',
              },
            ],
          },
          {
            path: '2022',
            component: lazy(() => import('@/pages/[org_id]/test')),
          },

          {
            path: 'self-serve',
            component: lazy(() => import('@/pages/[org_id]/self-serve/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/self-serve')),
              },
              {
                path: 'csv',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/upload')
                ),
              },
              {
                path: 'campaign/:id',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/marketing/email/campaigns/[id]/layout'
                    )
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/campaigns/[id]'
                        )
                    ),
                  },
                  {
                    path: 'reports',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/[id]/reports'
                            )
                        ),
                      },
                      {
                        path: 'sent',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/sent'
                            )
                        ),
                      },
                      {
                        path: 'events',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/events'
                            )
                        ),
                      },
                      {
                        path: 'failed',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/failed'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },

          {
            path: 'customer',
            component: lazy(() => import('@/pages/[org_id]/customer/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/customer')),
              },
              {
                path: 'profile-redirect/:id',
                component: lazy(
                  () => import('@/pages/[org_id]/customer/profile_redirect')
                ),
              },
              {
                path: ':id',
                component: lazy(
                  () => import('@/pages/[org_id]/customer/[id]/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]')
                    ),
                  },
                  {
                    path: 'bookings',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/bookings')
                    ),
                  },
                  {
                    path: 'gift',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/gift')
                    ),
                  },
                  {
                    path: 'reviews',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/reviews')
                    ),
                  },
                  {
                    path: 'marketing',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/marketing')
                    ),
                  },
                  {
                    path: 'transactions',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/customer/[id]/transactions')
                    ),
                  },
                  {
                    path: 'orders',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/orders')
                    ),
                  },
                  {
                    path: 'wi-fi',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/wi-fi')
                    ),
                  },
                  {
                    path: 'activity',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/activity')
                    ),
                  },
                  {
                    path: 'email',
                    component: lazy(
                      () => import('@/pages/[org_id]/customer/[id]/email')
                    ),
                  },
                ],
              },
            ],
          },

          /**
           * ALL INBOX ROUTES HERE
           */
          {
            path: 'inbox',
            component: lazy(() => import('@/pages/[org_id]/inbox/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/inbox')),
              },
              {
                path: 'threads',
                component: lazy(() => import('@/pages/[org_id]/inbox')),
              },
              {
                path: 'templates',
                component: lazy(
                  () => import('@/pages/[org_id]/inbox/templates')
                ),
              },
              {
                path: 'media',
                component: lazy(() => import('@/pages/[org_id]/inbox/media')),
              },
            ],
          },

          /**
           * ALL MARKETING ROUTES HERE
           */

          {
            path: 'marketing',
            component: lazy(() => import('@/pages/[org_id]/marketing/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/marketing')),
              },

              /**
               * NEW ROUTING START
               */
              {
                path: 'email',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/email/layout')
                ),
                children: [
                  {
                    path: 'campaigns',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/campaigns/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/email/campaigns')
                        ),
                      },
                      {
                        path: 'new',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/new'
                            )
                        ),
                      },
                      {
                        path: 'quick',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/quick'
                            )
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/campaigns/[id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/campaigns/[id]'
                                )
                            ),
                          },
                          {
                            path: 'revisions',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/campaigns/[id]/revisions'
                                )
                            ),
                          },
                          {
                            path: 'quick',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/campaigns/[id]/quick'
                                )
                            ),
                          },
                          {
                            path: 'reports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/email/campaigns/[id]/reports'
                                    )
                                ),
                              },
                              {
                                path: 'sent',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/sent'
                                    )
                                ),
                              },
                              {
                                path: 'events',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/events'
                                    )
                                ),
                              },
                              {
                                path: 'failed',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/email/campaigns/[id]/reports/failed'
                                    )
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'templates',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/templates/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/email/templates')
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/templates/[id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/templates/[id]'
                                )
                            ),
                          },
                          {
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/email/templates/[id]/revisions'
                                )
                            ),
                            path: 'revisions',
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'domains',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/domains/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/email/domains')
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/domains/[id]'
                            )
                        ),
                      },
                    ],
                  },
                  {
                    // BuilderV2 spike — Phase 0 of the email-builder PRD.
                    // Sandbox to iterate on the new editor before wiring it
                    // into real campaign IDs. See docs/email-builder-prd.md.
                    path: 'builder',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/email/builder')
                    ),
                  },
                  {
                    path: 'vouchers',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/marketing/foh/vouchers/layout')
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/foh/vouchers')
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/foh/vouchers/[id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]'
                                )
                            ),
                          },
                          {
                            path: 'performance',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/performance'
                                )
                            ),
                          },
                          {
                            path: 'imports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/imports'
                                )
                            ),
                          },
                          {
                            path: 'code-imports/:importId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/code-imports/:importId/import'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'audit',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/marketing/email/audit/layout')
                    ),
                    children: [
                      {
                        component: lazy(
                          () => import('@/pages/[org_id]/marketing/email/audit')
                        ),
                        index: true,
                      },
                    ],
                  },
                  {
                    path: 'verification-emails',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/verification-emails'
                        )
                    ),
                  },
                  {
                    path: 'workflows',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/email/workflows/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/email/workflows')
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/email/workflows/[id]'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },

              {
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/foh/layout')
                ),
                path: 'foh',
                children: [
                  {
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/foh/menus')
                    ),
                    index: true,
                  },
                  {
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/foh/menus')
                    ),
                    path: 'menus',
                    children: [
                      {
                        path: ':id',
                        component: lazy(
                          () => import('@/components/menus/menu.item')
                        ),
                      },
                    ],
                  },
                  {
                    path: 'vouchers',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/marketing/foh/vouchers/layout')
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/foh/vouchers')
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/foh/vouchers/[id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]'
                                )
                            ),
                          },
                          {
                            path: 'performance',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/performance'
                                )
                            ),
                          },
                          {
                            path: 'imports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/imports'
                                )
                            ),
                          },
                          {
                            path: 'code-imports/:importId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/foh/vouchers/[id]/code-imports/:importId/import'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/website/layout')
                ),
                path: 'website',
                children: [
                  {
                    path: 'footprint',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/website/footprint/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/footprint'
                            )
                        ),
                      },
                      {
                        path: 'customers',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/footprint/customers'
                            )
                        ),
                      },
                      {
                        path: 'settings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/footprint/settings'
                            )
                        ),
                      },
                    ],
                  },
                  {
                    path: 'forms',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/data/layout')
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/website/forms')
                        ),
                      },
                    ],
                  },
                  {
                    path: 'links',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/marketing/website/links/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/website/links')
                        ),
                      },
                      {
                        path: 'utm-planner',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/links/utm-planner'
                            )
                        ),
                      },
                      {
                        path: 'conversions',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/links/conversions'
                            )
                        ),
                      },
                      {
                        path: 'link-reports',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/links/link-reports'
                            )
                        ),
                      },
                      {
                        path: 'link-reports-new',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/website/links/link-reports-new'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
              /**
               * NEW ROUTING END
               */
              {
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/sms/layout')
                ),
                path: 'sms',
                children: [
                  {
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/sms/senders')
                    ),
                    path: 'senders',
                  },
                  {
                    path: 'audit',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/sms/audit')
                    ),
                    /*
                    children: [
                      {
                        path: ':id/events',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/sms/audit/'
                            )
                        ),
                      },
                    ],
                    */
                  },
                  {
                    path: 'ledger',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/sms/ledger')
                    ),
                  },
                  {
                    path: 'campaigns',
                    children: [
                      {
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketing/sms/campaigns')
                        ),
                        index: true,
                      },
                      {
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/marketing/sms/campaigns/[id]/layout'
                            )
                        ),
                        path: ':id',
                        children: [
                          {
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/sms/campaigns/[id]'
                                )
                            ),
                            index: true,
                          },
                          {
                            path: 'reports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketing/sms/campaigns/[id]/reports/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/sms/campaigns/[id]/reports'
                                    )
                                ),
                              },
                              {
                                path: 'sent',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/sms/campaigns/[id]/reports/sent'
                                    )
                                ),
                              },
                              {
                                path: 'events',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/sms/campaigns/[id]/reports/events'
                                    )
                                ),
                              },
                              {
                                path: 'failed',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/marketing/sms/campaigns/[id]/reports/failed'
                                    )
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },

              {
                path: 'data',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/page')
                ),
              },
              {
                path: 'data/segments/all',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/segment/all')
                ),
              },
              {
                path: 'data/segments/:id',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/segment/[id]')
                ),
              },
              {
                path: 'data/upload',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/upload')
                ),
              },
              {
                path: 'data/ideas',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/ideas')
                ),
              },
              {
                path: 'data/meta',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/data/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/data/meta/page')
                    ),
                  },
                ],
              },
              {
                path: 'data/segment-data-exports',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/marketing/data/segment-data-exports'
                    )
                ),
              },

              {
                path: 'audiences',
                component: lazy(
                  () => import('@/pages/[org_id]/marketing/audiences/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/audiences')
                    ),
                  },
                  {
                    path: 'new',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/audiences/new')
                    ),
                  },
                  {
                    path: ':id',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketing/audiences/[id]')
                    ),
                  },
                ],
              },

              {
                path: 'simple-segments',
                component: <Navigate to="../audiences" replace />,
              },
            ],
          },

          /**
           * ALL WORKFLOW ROUTES HERE - Moved to marketing section
           */
          {
            path: 'workflows',
            component: <Navigate to="../marketing/email/workflows" replace />,
          },
          {
            path: 'workflows/:id',
            component: lazy(
              () => import('@/pages/[org_id]/workflows-redirect')
            ),
          },

          /**
           * ALL REVIEWS ROUTES HERE
           */
          {
            path: 'reviews',
            component: lazy(() => import('@/pages/[org_id]/reviews/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/reviews/index')),
              },
              // Venue-only review surfaces mirrored at org level — show the
              // "choose a venue" gate when no venue is selected.
              {
                path: 'settings',
                component: (
                  <VenueGatePage
                    label="Page settings"
                    venuePath="reviews/settings"
                  />
                ),
              },
              {
                path: 'competitors',
                component: (
                  <VenueGatePage
                    label="Competitors"
                    venuePath="reviews/competitors"
                  />
                ),
              },
              {
                path: 'google',
                component: lazy(
                  () => import('@/pages/[org_id]/reviews/google/index')
                ),
              },
              {
                path: 'reports',
                component: lazy(
                  () => import('@/pages/[org_id]/reviews/reports/index')
                ),
              },
              {
                // The dashboard that used to be at `reports`. Kept, because
                // the platform mix, sentiment split and email funnel on it are
                // useful; just never the thing an owner reads first. The
                // article at `reports` links down to it.
                path: 'reports/metrics',
                component: lazy(
                  () => import('@/pages/[org_id]/reviews/reports/metrics')
                ),
              },
              {
                path: 'ai-settings',
                component: lazy(
                  () => import('@/pages/[org_id]/reviews/ai-settings/layout')
                ),
                children: [
                  {
                    index: true,
                    component: <Navigate to="auto-generation" replace />,
                  },
                  {
                    path: 'auto-generation',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/reviews/ai-settings/auto-generation'
                        )
                    ),
                  },
                  {
                    path: 'approval-queue',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/reviews/ai-settings/approval-queue'
                        )
                    ),
                  },
                  {
                    path: 'response-prompts',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/reviews/ai-settings/response-prompts'
                        )
                    ),
                  },
                  {
                    path: 'activity-log',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/reviews/ai-settings/activity-log'
                        )
                    ),
                  },
                  {
                    path: 'analytics',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reviews/ai-settings/analytics')
                    ),
                  },
                ],
              },
              {
                // Legacy alias — kept working via a redirect to the real
                // section routes (the page itself now lives under `ai-settings`).
                path: 'ai-settings-new',
                component: <Navigate to="../ai-settings" replace />,
              },
              {
                path: 'responses',
                component: lazy(
                  () => import('@/pages/[org_id]/reviews/responses/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/reviews/responses')
                    ),
                  },
                  {
                    path: ':id',
                    component: lazy(
                      () => import('@/pages/[org_id]/reviews/responses/[id]')
                    ),
                  },
                ],
              },
            ],
          },
          /**
           * ALL VENUES ROUTES HERE
           */
          {
            path: 'venues',
            component: lazy(() => import('@/pages/[org_id]/venues/layout')),

            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/venues/index')),
              },
              {
                path: ':serial',
                component: lazy(
                  () => import('@/pages/[org_id]/venues/[serial]/layout')
                ),
                handle: {
                  crumb: (data: any) => {
                    return <VenueSwitchCrumb data={data} />
                  },
                },
                children: [
                  {
                    index: true,
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/venues/[serial]/index-route')
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                  },
                  {
                    // Full Bhuna at VENUE scope. Same layout and same section components as
                    // the org-scoped routes — the layout reads the serial out of the URL,
                    // scopes every query to it and drops its own venue picker, so there is
                    // no venue-specific copy of any of this to keep in step.
                    path: 'reports',
                    children: [
                      {
                        path: 'full-bhuna',
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/reports/full-bhuna/layout')
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import('@/pages/[org_id]/reports/full-bhuna')
                            ),
                          },
                          {
                            path: 'bookings',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/bookings'
                                )
                            ),
                          },
                          {
                            path: 'revenue',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/revenue'
                                )
                            ),
                          },
                          {
                            path: 'deposits',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/deposits'
                                )
                            ),
                          },
                          {
                            path: 'marketing',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/marketing'
                                )
                            ),
                          },
                          {
                            path: 'reviews',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/reviews'
                                )
                            ),
                          },
                          {
                            path: 'wifi',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/wifi'
                                )
                            ),
                          },
                          {
                            path: 'inbox',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/inbox'
                                )
                            ),
                          },
                          {
                            path: 'enquiries',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/enquiries'
                                )
                            ),
                          },
                          {
                            path: 'loyalty',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/full-bhuna/loyalty'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    // Know your customer at VENUE scope. The same page as the
                    // org route: it reads the serial from the URL, scopes its
                    // one query to it and drops its own venue picker. Guarded
                    // here rather than trusting the nav to hide the link.
                    path: 'know-your-customer',
                    capability: 'kyc.view',
                    component: lazy(
                      () => import('@/pages/[org_id]/know-your-customer')
                    ),
                  },
                  {
                    path: 'in-the-building',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/in-the-building'
                        )
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                  },
                  {
                    path: 'square',
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () => import('@/pages/[org_id]/square')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                      {
                        path: 'table-mappings',
                        component: lazy(
                          () => import('@/pages/[org_id]/square/mappings')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                      {
                        // Retired: Square > Table Ordering mounted the Zion
                        // catalogue editor (the old Bookings > Menus pages).
                        // Menus live in Ordering (Mouse) now, so every old
                        // URL lands on this venue's Ordering > Menus.
                        path: 'ordering/*',
                        component: <MenusMovedToOrdering />,
                      },
                      {
                        path: 'discounts',
                        component: lazy(
                          () => import('@/pages/[org_id]/square/discounts')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                      {
                        path: 'loyalty',
                        component: lazy(
                          () => import('@/pages/[org_id]/square/loyalty')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                      {
                        path: 'customer-groups',
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/square/customer-groups')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                      {
                        path: 'traces',
                        component: lazy(
                          () => import('@/pages/[org_id]/square/traces')
                        ),
                        handle: {
                          crumb: (data: any) => {
                            return <VenueCrumb data={data} />
                          },
                        },
                      },
                    ],
                  },
                  {
                    // Ordering (Mouse), venue-scoped mount: the same pages with
                    // the venue preset as the filter.
                    path: 'ordering',
                    capability: 'nav.ordering',
                    component: lazy(
                      () => import('@/pages/[org_id]/ordering/layout')
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                    children: [
                      {
                        index: true,
                        // Pre-ordering, as at org level.
                        component: (
                          <Navigate to="orders/pre-ordering" replace />
                        ),
                      },
                      {
                        path: 'orders',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/orders')
                        ),
                      },
                      // The two channel-scoped views, before `:orderId`.
                      {
                        path: 'orders/pre-ordering',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/orders')
                        ),
                      },
                      {
                        path: 'orders/table-ordering',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/orders')
                        ),
                      },
                      {
                        path: 'orders/:orderId',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/orders')
                        ),
                      },
                      {
                        path: 'menus',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/menus')
                        ),
                      },
                      {
                        path: 'menus/:menuId',
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/ordering/menus/[menuId]')
                        ),
                      },
                      {
                        path: 'menus/:menuId/report',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/ordering/menus/[menuId]/report'
                            )
                        ),
                      },
                      {
                        path: 'menus/:menuId/replay',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/ordering/menus/[menuId]/replay'
                            )
                        ),
                      },
                      {
                        path: 'menus/:menuId/ai',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/ordering/menus/[menuId]/ai'
                            )
                        ),
                      },
                      {
                        path: 'venue-space/:serial',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/venue-space')
                        ),
                      },
                      {
                        path: 'products',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/products')
                        ),
                      },
                      {
                        path: 'modifiers',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/modifiers')
                        ),
                      },
                      {
                        path: 'tables',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/tables')
                        ),
                      },
                      {
                        path: 'tables/print',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/tables/print')
                        ),
                      },
                      {
                        // The venue twin, which passes its serial down. The
                        // other Ordering pages reuse the org page and read the
                        // scope hook; place cards predates that and takes the
                        // serial as a prop.
                        path: 'placecards',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/ordering/placecards'
                            )
                        ),
                      },
                      {
                        path: 'sms',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/sms')
                        ),
                      },
                      {
                        path: 'app-feedback',
                        capability: 'internal.staff',
                        component: lazy(
                          () => import('@/pages/[org_id]/ordering/app-feedback')
                        ),
                      },
                    ],
                  },
                  {
                    path: 'inbox',
                    component: lazy(() => import('@/pages/[org_id]/inbox')),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                  },
                  {
                    path: 'reviews',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/reviews/layout'
                        )
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },

                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/venues/[serial]/reviews')
                        ),
                      },

                      {
                        path: 'settings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/settings'
                            )
                        ),
                      },

                      {
                        path: 'boost',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/boost'
                            )
                        ),
                      },

                      {
                        path: 'reports',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/reports'
                            )
                        ),
                      },
                      {
                        // The per-venue dashboard that used to be at `reports`.
                        path: 'reports/metrics',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/reports/metrics'
                            )
                        ),
                      },

                      {
                        path: 'responses',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/responses/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/responses'
                                )
                            ),
                          },
                          {
                            path: ':id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/responses/[id]'
                                )
                            ),
                          },
                        ],
                      },

                      {
                        path: 'google',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/google'
                            )
                        ),
                      },

                      {
                        path: 'competitors',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/competitors'
                            )
                        ),
                      },

                      {
                        path: 'ai-settings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: (
                              <Navigate to="auto-generation" replace />
                            ),
                          },
                          {
                            path: 'auto-generation',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/auto-generation'
                                )
                            ),
                          },
                          {
                            path: 'approval-queue',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/approval-queue'
                                )
                            ),
                          },
                          {
                            path: 'response-prompts',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/response-prompts'
                                )
                            ),
                          },
                          {
                            path: 'activity-log',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/activity-log'
                                )
                            ),
                          },
                          {
                            path: 'analytics',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/reviews/ai-settings/analytics'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        // Legacy alias — kept working via a redirect to the
                        // real section routes.
                        path: 'ai-settings-new',
                        component: <Navigate to="../ai-settings" replace />,
                      },
                    ],
                  },
                  {
                    path: 'brand',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/venues/[serial]/brand/layout')
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () => import('@/pages/[org_id]/venues/[serial]/brand')
                        ),
                      },
                      {
                        path: 'wi-fi',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/branding'
                            )
                        ),
                      },
                      {
                        path: 'email',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/email'
                            )
                        ),
                      },
                      {
                        path: 'reviews',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/reviews'
                            )
                        ),
                      },
                      {
                        path: 'bookings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/booking'
                            )
                        ),
                      },
                      {
                        path: 'gift-cards',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/gift-cards'
                            )
                        ),
                      },
                      {
                        path: 'forms',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/forms'
                            )
                        ),
                      },
                      {
                        path: 'journey',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/brand/journey'
                            )
                        ),
                      },
                    ],
                  },
                  {
                    path: 'wi-fi',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/venues/[serial]/wi-fi/layout')
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },

                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/index'
                            )
                        ),
                      },
                      {
                        path: 'networks',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/networks'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/networks/status/page'
                                )
                            ),
                          },
                          {
                            path: 'status',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/networks/status/page'
                                )
                            ),
                          },
                          {
                            path: 'vpn',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/networks/vpn/page'
                                )
                            ),
                          },
                          {
                            path: 'settings',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/networks/settings/page'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'ip-addresses',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/ip-addresses'
                            )
                        ),
                      },
                      {
                        path: 'firewall',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/firewall'
                            )
                        ),
                      },
                      {
                        path: 'insight',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/insight'
                            )
                        ),
                      },
                      {
                        path: 'access-points',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/access-points'
                            )
                        ),
                      },
                      {
                        // Retired top-level surface — folded into the hardware
                        // group. Redirect by tier so no orphaned page.
                        path: 'activity',
                        component: (
                          <RedirectByTier
                            snapTo="snap-guard"
                            hardwareTo="hardware"
                          />
                        ),
                      },
                      {
                        path: 'snap-guard',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/sessions'
                                )
                            ),
                          },
                          {
                            path: 'access-points',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/access-points-page'
                                )
                            ),
                          },
                          {
                            path: 'payments',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/payments-page'
                                )
                            ),
                          },
                          {
                            path: 'analytics',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/analytics-page'
                                )
                            ),
                          },
                          {
                            path: 'networks',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/networks-page'
                                )
                            ),
                          },
                          {
                            path: 'rate-limits',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/rate-limits-page'
                                )
                            ),
                          },
                          {
                            path: 'firewall',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/firewall-page'
                                )
                            ),
                          },
                          {
                            // Firmware merged into Health & Build.
                            path: 'firmware',
                            component: <Navigate to="../health" replace />,
                          },
                          {
                            path: 'scan',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/scan-page'
                                )
                            ),
                          },
                          {
                            path: 'console',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/console-page'
                                )
                            ),
                          },
                          {
                            path: 'staff-comms',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/staff-comms-page'
                                )
                            ),
                          },
                          {
                            path: 'camera-wall',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/camera-wall-page'
                                )
                            ),
                          },
                          {
                            path: 'health',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/pages/health-page'
                                )
                            ),
                          },
                          {
                            // Device Health merged into Health & Build.
                            path: 'device-health',
                            component: <Navigate to="../health" replace />,
                          },
                          {
                            path: 'setup',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/snap-guard/setup'
                                )
                            ),
                          },
                          // ── Legacy redirects ──
                          {
                            path: 'activity',
                            component: <Navigate to=".." replace />,
                          },
                          {
                            path: 'overview',
                            component: <Navigate to=".." replace />,
                          },
                          {
                            path: 'settings',
                            component: <Navigate to="../rate-limits" replace />,
                          },
                          {
                            path: 'policies',
                            component: <Navigate to="../rate-limits" replace />,
                          },
                        ],
                      },
                      {
                        path: 'status',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/status/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/status/page'
                                )
                            ),
                          },
                          {
                            path: 'vpn',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/status/vpn/page'
                                )
                            ),
                          },
                          {
                            path: 'snap-guard',
                            component: (
                              <Navigate to="../../snap-guard" replace />
                            ),
                          },
                          {
                            path: 'snapguard',
                            component: (
                              <Navigate to="../../snap-guard" replace />
                            ),
                          },
                          {
                            path: ':id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/status/[id]/page'
                                )
                            ),
                          },
                          {
                            path: 'settings',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/status/settings/page'
                                )
                            ),
                          },
                          {
                            // Topology retired as a top-level surface — the
                            // diagram now lives in SnapGuard Health. Redirect
                            // by tier.
                            path: 'network-topology',
                            component: (
                              <RedirectByTier
                                snapTo="snap-guard/health"
                                hardwareTo="hardware"
                              />
                            ),
                          },
                        ],
                      },
                      {
                        path: 'sessions',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/sessions'
                            )
                        ),
                      },
                      {
                        path: 'settings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/settings'
                            )
                        ),
                      },
                      {
                        path: 'payments',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/payments'
                            )
                        ),
                      },

                      {
                        path: 'upsell',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/upsell/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/upsell'
                                )
                            ),
                          },
                          {
                            path: 'events',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/upsell/events'
                                )
                            ),
                          },
                          {
                            path: 'performance',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/upsell/performance'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'survey',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/survey'
                            )
                        ),
                      },

                      {
                        path: 'hardware',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/hardware/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/hardware'
                                )
                            ),
                          },
                          {
                            path: 'setup',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/hardware/setup'
                                )
                            ),
                          },
                          {
                            // Click-through hardware: portal-auth connections
                            // (no usage/accounting data — see the page notice).
                            path: 'connections',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/hardware/connections'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'design',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/wi-fi/branding/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/wi-fi/branding'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },

                  {
                    path: 'bookings',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/bookings/layout'
                        )
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },

                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/index-route'
                            )
                        ),
                      },
                      {
                        // Retired: Menus, Items and Modifier Groups were the
                        // Zion catalogue editor. Every organisation edits its
                        // catalogue in Ordering (Mouse) now, so the whole
                        // subtree redirects. Square > Table Ordering, which
                        // mounted the same pages, redirects too.
                        path: 'menus/*',
                        component: <MenusMovedToOrdering />,
                      },
                      {
                        path: 'settings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/settings/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/main-config'
                                )
                            ),
                          },
                          {
                            path: 'embed-links',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/embed-links/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/embed-links'
                                    )
                                ),
                              },
                              {
                                path: 'stand-alone-booking',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/embed-links/stand-alone-booking'
                                    )
                                ),
                              },
                              {
                                path: 'tablet-mode',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/embed-links/tablet-mode'
                                    )
                                ),
                              },
                              {
                                path: 'embed-widget',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/embed-links/widget'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            // Layout (shared form spine) + one page per
                            // section, mirroring reviews/ai-settings.
                            path: 'main-configuration',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/main-configuration'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: <Navigate to="basic" replace />,
                              },
                              {
                                path: ':sectionId',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/main-configuration/section-page'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'areas',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/areas'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'combinations',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/combinations/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/combinations'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'booking-types',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/booking-types/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/booking-types'
                                    )
                                ),
                              },

                              {
                                path: 'archived',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/booking-types/archived'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'booking-types/new',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/booking-types/edit'
                                )
                            ),
                          },
                          {
                            path: 'booking-types/:typeId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/booking-types/edit'
                                )
                            ),
                          },
                          {
                            path: 'google',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/google/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/google/settings'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'automations',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/automations/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/automations/settings'
                                    )
                                ),
                              },
                              {
                                path: 'stats',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/automations/stats'
                                    )
                                ),
                              },
                              {
                                path: ':automation_source_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/automations/settings/[id]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/automations/settings/[id]'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },

                          {
                            path: 'concierge',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/concierge'
                                )
                            ),
                          },
                          {
                            path: 'email-templates',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/email-templates'
                                )
                            ),
                          },
                          {
                            path: 'payment-reminders',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/payment-reminders'
                                )
                            ),
                          },
                          {
                            path: 'tables',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/index-route'
                                    )
                                ),
                              },
                              {
                                path: 'archived',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/archived'
                                    )
                                ),
                              },
                              {
                                path: 'areas',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas'
                                        )
                                    ),
                                  },
                                  {
                                    path: 'archived',
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/archived'
                                        )
                                    ),
                                  },
                                ],
                              },

                              {
                                path: 'combinations',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/combinations/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/combinations'
                                        )
                                    ),
                                  },
                                  {
                                    path: 'archived',
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/combinations/archived'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: 'areas',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/index'
                                        )
                                    ),
                                  },
                                  {
                                    path: 'archived',
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/bookings/settings/tables/areas/archived'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },

                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/covers'
                                )
                            ),
                          },
                          {
                            path: 'covers',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/covers'
                                )
                            ),
                          },
                          {
                            path: 'intervals',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/intervals'
                                )
                            ),
                          },
                          {
                            path: 'details',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/details'
                                )
                            ),
                          },
                          {
                            path: 'user-journey',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/user-journey'
                                )
                            ),
                          },
                          {
                            path: 'user-actions',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/user_actions'
                                )
                            ),
                          },
                          {
                            path: 'options',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/options'
                                )
                            ),
                          },
                          {
                            path: 'service-hours',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/service-hours'
                                )
                            ),
                          },
                          {
                            path: 'reminders',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/reminders'
                                )
                            ),
                          },

                          {
                            path: 'day-planner',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/day-planner'
                                )
                            ),
                          },

                          {
                            path: 'payments',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/forms/payments'
                                )
                            ),
                          },
                          {
                            path: 'messages',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/messages'
                                )
                            ),
                          },
                          {
                            path: 'tracking',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/tracking'
                                )
                            ),
                          },
                          {
                            path: 'time-slot-capacities',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/time-slot-capacities'
                                )
                            ),
                          },
                          {
                            path: 'simulation',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/simulation'
                                )
                            ),
                          },
                          {
                            path: 'items',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/items'
                                )
                            ),
                          },
                          {
                            path: 'special-dates',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/special-dates/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/special-dates/dates'
                                    )
                                ),
                              },

                              {
                                path: 'archived',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/settings/special-dates/archived'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'special-dates/new',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/special-dates/edit'
                                )
                            ),
                          },
                          {
                            path: 'special-dates/:dateId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/special-dates/edit'
                                )
                            ),
                          },
                          {
                            path: 'enquiries',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/enquiries'
                                )
                            ),
                          },
                          {
                            path: 'events',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events'
                                )
                            ),
                          },
                          {
                            path: 'events/new',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/edit'
                                )
                            ),
                          },
                          {
                            path: 'events/:eventId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/edit'
                                )
                            ),
                          },
                          // The ticketing section for one event (D19). Sits under
                          // the event rather than at the top level because tiers,
                          // orders and the door list have no meaning without one.
                          {
                            path: 'events/:eventId/tickets',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets'
                                )
                            ),
                          },
                          {
                            path: 'events/:eventId/tickets/orders/:orderId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets/order'
                                )
                            ),
                          },
                          {
                            path: 'events/:eventId/door',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets/door'
                                )
                            ),
                          },
                          // Ticketed events: their own section, because a table
                          // form is mostly noise for a gig and capacity has to be
                          // settable while creating one.
                          {
                            path: 'ticketed-events',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/ticketed-events'
                                )
                            ),
                          },
                          {
                            path: 'ticketed-events/new',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/ticketed-events/edit'
                                )
                            ),
                          },
                          {
                            path: 'ticketed-events/:eventId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/ticketed-events/edit'
                                )
                            ),
                          },
                          // The same Tickets and Door pages, reachable from this
                          // section too. They read the section off the path so
                          // their back buttons return to the right list.
                          {
                            path: 'ticketed-events/:eventId/tickets',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets'
                                )
                            ),
                          },
                          {
                            path: 'ticketed-events/:eventId/tickets/orders/:orderId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets/order'
                                )
                            ),
                          },
                          {
                            path: 'ticketed-events/:eventId/door',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/events/tickets/door'
                                )
                            ),
                          },
                          {
                            path: 'upgrades',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/upgrades'
                                )
                            ),
                          },
                          {
                            path: 'upgrades/new',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/upgrades/edit'
                                )
                            ),
                          },
                          {
                            path: 'upgrades/:upgradeId',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/upgrades/edit'
                                )
                            ),
                          },
                          {
                            path: 'wallet',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/wallet'
                                )
                            ),
                          },
                          {
                            // Declared before `wallet/:id` so the reading order
                            // matches the resolution order. React Router ranks a
                            // static segment above a dynamic one regardless, but
                            // `passes` and `downloads` are both valid `:id`
                            // shapes, so the ordering is not left implicit.
                            path: 'wallet/passes',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/wallet/passes'
                                )
                            ),
                          },
                          {
                            path: 'wallet/downloads',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/wallet/downloads'
                                )
                            ),
                          },
                          {
                            // `new` resolves against the same designer page, which
                            // treats the missing id as a blank card style.
                            path: 'wallet/:id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/settings/wallet/[id]'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'setup-wizard',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/setup-wizard'
                            )
                        ),
                      },
                      {
                        path: 'configuration',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/config/[id]/settings/configuration/layout'
                            )
                        ),
                      },

                      {
                        path: 'list',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/list'
                            )
                        ),
                      },
                      {
                        path: 'inbox',
                        component: lazy(() => import('@/pages/[org_id]/inbox')),
                      },

                      {
                        path: 'day-planner',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/day-planner/layout'
                            )
                        ),
                        children: [
                          {
                            path: 'inbox',
                            component: lazy(
                              () => import('@/pages/[org_id]/inbox')
                            ),
                          },
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/day-planner'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'day-planner-fast',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/day-planner-fast/layout'
                            )
                        ),
                        // Planner-shaped skeleton while the JS chunks
                        // download — dependency-light, safe to import
                        // statically (doesn't drag the planner chunk in).
                        fallback: <DayPlannerSkeleton />,
                        children: [
                          {
                            path: 'inbox',
                            component: lazy(
                              () => import('@/pages/[org_id]/inbox')
                            ),
                          },
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/day-planner-fast'
                                )
                            ),
                            fallback: <DayPlannerSkeleton />,
                          },
                        ],
                      },
                      {
                        path: 'day-planner-3',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/day-planner-3/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/day-planner-3'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'outstanding-payments',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/outstanding-payments'
                            )
                        ),
                      },
                      {
                        path: 'booking-alerts',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/booking-alerts'
                            )
                        ),
                      },
                      {
                        path: 'optimisation',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/optimisation'
                            )
                        ),
                      },
                      {
                        path: 'csv-imports',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/csv-imports'
                            )
                        ),
                      },
                      {
                        path: 'csv-exports',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/csv-exports'
                            )
                        ),
                      },
                      {
                        path: 'csv-imports/new',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/csv-imports/new'
                            )
                        ),
                      },
                      {
                        path: 'csv-imports/:id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/csv-imports/[id]'
                            )
                        ),
                      },
                      {
                        path: 'live-migration',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/live-migration'
                            )
                        ),
                      },
                      {
                        path: 'list-view-fast',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/list-view-fast/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/list-view-fast'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'list-view-3',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/list-view-3/layout'
                            )
                        ),
                        // List-shaped skeleton while the JS chunk downloads —
                        // dependency-light, safe to import statically (doesn't
                        // drag the page chunk in).
                        fallback: <ListViewSkeleton />,
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/list-view-3'
                                )
                            ),
                            fallback: <ListViewSkeleton />,
                          },
                        ],
                      },
                      {
                        path: 'floorplan-fast',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/floorplan-fast/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/floorplan-fast'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'floorplan-3',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/floorplan-3/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/floorplan-3'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'floorplan-3-edit',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/floorplan-3-edit/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/floorplan-3-edit'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'boost',
                        capability: 'internal.boost',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/boost'
                            )
                        ),
                      },
                      {
                        path: 'voice',
                        capability: 'bookings.voice',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/voice/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/voice'
                                )
                            ),
                          },
                          {
                            path: 'calls',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/voice/calls'
                                )
                            ),
                          },
                          {
                            path: 'reporting',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/voice/reporting'
                                )
                            ),
                          },
                          {
                            // A call is a thing people send each other, so it
                            // gets its own address rather than a sheet.
                            path: 'calls/:call_id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/voice/calls/[call_id]'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        // Enquiries workspace, venue scope (see the org node).
                        path: 'enquiries-workspace',
                        // Same gate as the org-level route above: staff plus the
                        // named early-access users in the policy. The nav sends a
                        // user inside a venue here, so both copies must agree.
                        capability: 'bookings.enquiries-workspace',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace'
                                )
                            ),
                          },
                          {
                            path: 'board',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/board'
                                )
                            ),
                          },
                          {
                            path: 'today',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/today'
                                )
                            ),
                          },
                          {
                            path: 'reports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/reports'
                                )
                            ),
                          },
                          {
                            path: 'list',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/list'
                                )
                            ),
                          },
                          {
                            path: 'calendar',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/calendar'
                                )
                            ),
                          },
                          {
                            path: 'scheduled',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/scheduled'
                                )
                            ),
                          },
                          {
                            path: 'workflows',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/workflows'
                                )
                            ),
                          },
                          {
                            path: 'run-sheet',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/run-sheet'
                                )
                            ),
                          },
                          {
                            path: ':id/function-sheet',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/[id]/function-sheet'
                                )
                            ),
                          },
                          {
                            path: ':id/run-sheet',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/[id]/run-sheet'
                                )
                            ),
                          },
                          {
                            path: 'day-plan',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/day-plan'
                                )
                            ),
                          },
                          {
                            path: 'money',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/money'
                                )
                            ),
                          },
                          {
                            path: 'files',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/files'
                                )
                            ),
                          },
                          {
                            path: 'coordinator',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/coordinator'
                                )
                            ),
                          },
                          {
                            path: 'stages',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/stages'
                                )
                            ),
                          },
                          {
                            path: ':id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries-workspace/[id]'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'enquiries',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/enquiries/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries'
                                )
                            ),
                          },
                          {
                            path: 'reports',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries/reports'
                                )
                            ),
                          },
                          {
                            path: ':id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/enquiries/[id]'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'deposits',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/deposits'
                            )
                        ),
                      },
                      {
                        // Moved to Ordering; see the org-level twin.
                        path: 'placecards',
                        component: <PlacecardsMovedToOrdering />,
                      },
                      {
                        path: 'reports',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/reports'
                            )
                        ),
                      },
                      {
                        path: 'fast-view',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/fast-view/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/fast-view'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'floor-plans-edit',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/floor-plans-edit/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/floor-plans-edit'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'design',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/design'
                            )
                        ),
                      },
                      {
                        path: 'design/branding',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/design/branding'
                            )
                        ),
                      },
                      {
                        path: 'floor-plans',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/floor-plans/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/floor-plans'
                                )
                            ),
                          },
                        ],
                      },

                      {
                        path: 'reporting',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/reporting/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/reporting'
                                )
                            ),
                          },
                          {
                            path: ':report_id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/bookings/reporting/[report_id]/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/bookings/reporting/[report_id]'
                                    )
                                ),
                              },
                            ],
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'loyalty',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/loyalty/layout'
                        )
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/venues/[serial]/loyalty')
                        ),
                      },
                      // Venue-scoped twins of the org-level scheme gates —
                      // `SchemeGatePage` reads the serial from the URL and
                      // keeps you in the venue scope after picking.
                      {
                        path: 'overview',
                        component: <SchemeGatePage label="Overview" />,
                      },
                      {
                        path: 'customers',
                        component: (
                          <SchemeGatePage
                            label="Enrolled Guests"
                            service="customers"
                          />
                        ),
                      },
                      {
                        path: 'history',
                        component: (
                          <SchemeGatePage
                            label="Interaction History"
                            service="history"
                          />
                        ),
                      },
                      {
                        path: 'rewards',
                        component: (
                          <SchemeGatePage label="Rewards" service="rewards" />
                        ),
                      },
                      {
                        path: 'vouchers',
                        component: (
                          <SchemeGatePage
                            label="Reward Vouchers"
                            service="vouchers"
                          />
                        ),
                      },
                      {
                        path: 'links',
                        component: (
                          <SchemeGatePage
                            label="Link Builder"
                            service="links"
                          />
                        ),
                      },
                      {
                        path: 'gift-cards',
                        component: (
                          <SchemeGatePage
                            label="Square Gift Cards"
                            service="gift-cards"
                          />
                        ),
                      },
                      {
                        path: 'member-imports',
                        component: (
                          <SchemeGatePage
                            label="Import Members"
                            service="member-imports"
                          />
                        ),
                      },
                      {
                        path: 'preview-scheme',
                        component: (
                          <SchemeGatePage
                            label="Preview Scheme"
                            service="preview-scheme"
                          />
                        ),
                      },
                      {
                        path: 'preview-rewards',
                        component: (
                          <SchemeGatePage
                            label="Preview Rewards"
                            service="preview-rewards"
                          />
                        ),
                      },
                      {
                        path: ':loyalty_scheme_id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () => import('@/pages/[org_id]/loyalty/[id]')
                            ),
                          },
                          {
                            path: 'service',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/service/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/service'
                                    )
                                ),
                              },
                              {
                                path: ':loyalty_scheme_user_profile_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/customers/[loyalty_scheme_user_profile_id]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/customers/[loyalty_scheme_user_profile_id]'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/go-live/layout'
                                )
                            ),
                            path: 'go-live',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/go-live'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'customers',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/customers/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/customers'
                                    )
                                ),
                              },
                              {
                                path: ':loyalty_scheme_user_profile_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/customers/[loyalty_scheme_user_profile_id]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/customers/[loyalty_scheme_user_profile_id]'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            path: 'history',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/history'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'automations',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/automations/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/automations'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'rewards',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/rewards'
                                    )
                                ),
                              },
                              {
                                path: 'create',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/create/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/create'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: ':reward_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/[reward_id]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/[reward_id]'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: 'archived',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/archived/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/archived'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: 'vouchers',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/vouchers/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/vouchers'
                                        )
                                    ),
                                  },
                                  {
                                    path: 'upload',
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/vouchers/upload/layout'
                                        )
                                    ),
                                    children: [
                                      {
                                        index: true,
                                        component: lazy(
                                          () =>
                                            import(
                                              '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/rewards/vouchers/upload'
                                            )
                                        ),
                                      },
                                    ],
                                  },
                                  // {
                                  //   path: ":voucher_id",
                                  //   component: "[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/vouchers/[voucher_id]/layout",
                                  //   children: [
                                  //     {
                                  //       index: true,
                                  //       component: "[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/vouchers/[voucher_id]",
                                  //     },
                                  //   ],
                                  // },
                                  // {
                                  //   path: "archived",
                                  //   component: "[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/vouchers/archived/layout",
                                  //   children: [
                                  //     {
                                  //       index: true,
                                  //       component: "[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/vouchers/archived",
                                  //     },
                                  //   ],
                                  // },
                                ],
                              },
                            ],
                          },
                          {
                            path: 'vouchers',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/vouchers'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'links',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/links'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'member-imports',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/member-imports'
                                    )
                                ),
                              },
                              {
                                path: ':member_import_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/member-imports/[member_import_id]'
                                    )
                                ),
                              },
                            ],
                          },
                          // Venue-scoped twins of the microsite preview.
                          {
                            path: 'preview-scheme',
                            component: (
                              <LoyaltySchemePreviewPage variant="sign-up" />
                            ),
                          },
                          {
                            path: 'preview-rewards',
                            component: (
                              <LoyaltySchemePreviewPage variant="rewards" />
                            ),
                          },
                          {
                            path: 'gift-cards',
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/loyalty/[id]/gift-cards'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'gifts',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts'
                                    )
                                ),
                              },
                              {
                                path: 'archived',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/archived/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/archived'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: ':gift_id',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/[gift_id]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/[gift_id]'
                                        )
                                    ),
                                  },
                                ],
                              },
                              {
                                path: 'create',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/create/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/gifts/create'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },

                          {
                            path: 'passes',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/passes/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/passes'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'staff',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/staff/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/staff'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'redeem',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/redeem/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/redeem'
                                    )
                                ),
                              },
                              {
                                path: ':encrypted_qr_code_data',
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/redeem/[encrypted_qr_code_data]/layout'
                                    )
                                ),
                                children: [
                                  {
                                    index: true,
                                    component: lazy(
                                      () =>
                                        import(
                                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/redeem'
                                        )
                                    ),
                                  },
                                ],
                              },
                            ],
                          },
                          {
                            path: 'points',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/points/layout'
                                )
                            ),
                            children: [
                              {
                                index: true,
                                component: lazy(
                                  () =>
                                    import(
                                      '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/points'
                                    )
                                ),
                              },
                            ],
                          },
                          {
                            path: 'points-catchup',
                            capability: 'loyalty.points-catchup',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/points-catchup'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'settings',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/settings/layout'
                        )
                    ),
                  },

                  {
                    path: 'reports',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/reports/layout'
                        )
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                    children: [
                      {
                        path: 'interactions',
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/interactions')
                        ),
                        children: [
                          {
                            path: ':id',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/reports/interactions/[id]'
                                )
                            ),
                          },
                        ],
                      },
                      {
                        path: 'customers',
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/customers')
                        ),
                      },
                      {
                        path: 'origin',
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/origin')
                        ),
                      },
                      {
                        path: 'transactions',
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/transactions')
                        ),
                      },
                      {
                        index: true,
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/customers')
                        ),
                      },

                      {
                        path: 'customers',
                        component: lazy(
                          () => import('@/pages/[org_id]/reports/customers')
                        ),
                      },
                      /*
                      {
                        path: 'bandwidth',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reports/bandwidth'
                            )
                        ),
                      },
*/
                      {
                        path: 'reviews',
                        component: lazy(
                          () => import('@/pages/[org_id]/reviews/reports/index')
                        ),
                      },
                      {
                        path: 'bookings',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/bookings/reporting/stats'
                            )
                        ),
                      },
                      {
                        path: 'organisation',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/reports/two-point-zero/page'
                            )
                        ),
                      },
                      {
                        path: 'monthly-snapshot',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/reports/monthly-snapshot/page'
                            )
                        ),
                      },
                    ],
                  },
                  {
                    path: 'site',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/venues/[serial]/site/layout')
                    ),
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/venues/[serial]/site/page')
                        ),
                      },
                      {
                        path: 'content/:content_id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/site/content/[content_id]/page'
                            )
                        ),
                      },
                      {
                        path: 'gallery/:gallery_id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/site/gallery/[gallery_id]/page'
                            )
                        ),
                      },
                    ],
                  },
                  {
                    path: 'terminal',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/terminal/layout'
                        )
                    ),
                    children: [
                      {
                        path: '',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/terminal/page'
                            )
                        ),
                      },
                      {
                        path: ':order_id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/venues/[serial]/terminal/page'
                            )
                        ),
                      },
                    ],
                    handle: {
                      crumb: (data: any) => {
                        return <VenueCrumb data={data} />
                      },
                    },
                  },
                ],
              },
            ],
          },
          /**
           * LOYALTY OTHER ROUTES HERE
           *
           *  {
    path: '/gifting',
    component: AsyncGifting,
  },
  {
    path: '/gifting/activations',
    component: AsyncGiftingActivations,
  },
  {
    path: '/gifting/reports',
    component: AsyncGiftingReports,
  },
  {
    path: '/gifting/:id',
    component: AsyncGiftingSingle,
  },

           */
          {
            path: 'loyalty',
            component: lazy(() => import('@/pages/[org_id]/loyalty/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/loyalty')),
              },
              {
                path: 'new',
                component: lazy(() => import('@/pages/[org_id]/loyalty/new')),
              },
              {
                path: 'reports',
                component: lazy(
                  () => import('@/pages/[org_id]/loyalty/reports')
                ),
              },
              // Scheme-less service routes. Every loyalty service lives under a
              // scheme, so these render the "pick a scheme to continue" gate and
              // forward to the same service once one is chosen — the venue-gate
              // pattern, applied to schemes. Static segments outrank `:id`, and
              // `parseSchemeId` skips them, so they never read as a scheme.
              {
                path: 'overview',
                component: <SchemeGatePage label="Overview" />,
              },
              {
                path: 'customers',
                component: (
                  <SchemeGatePage label="Enrolled Guests" service="customers" />
                ),
              },
              {
                path: 'history',
                component: (
                  <SchemeGatePage
                    label="Interaction History"
                    service="history"
                  />
                ),
              },
              {
                path: 'rewards',
                component: <SchemeGatePage label="Rewards" service="rewards" />,
              },
              {
                path: 'vouchers',
                component: (
                  <SchemeGatePage label="Reward Vouchers" service="vouchers" />
                ),
              },
              {
                path: 'links',
                component: (
                  <SchemeGatePage label="Link Builder" service="links" />
                ),
              },
              {
                path: 'gift-cards',
                component: (
                  <SchemeGatePage
                    label="Square Gift Cards"
                    service="gift-cards"
                  />
                ),
              },
              {
                path: 'member-imports',
                component: (
                  <SchemeGatePage
                    label="Import Members"
                    service="member-imports"
                  />
                ),
              },
              {
                path: 'preview-scheme',
                component: (
                  <SchemeGatePage
                    label="Preview Scheme"
                    service="preview-scheme"
                  />
                ),
              },
              {
                path: 'preview-rewards',
                component: (
                  <SchemeGatePage
                    label="Preview Rewards"
                    service="preview-rewards"
                  />
                ),
              },
              {
                path: ':id',
                component: lazy(
                  () => import('@/pages/[org_id]/loyalty/[id]/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]')
                    ),
                  },
                  {
                    path: 'customers',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/customers')
                    ),
                  },
                  {
                    path: 'redemptions',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/redemptions')
                    ),
                  },
                  {
                    path: 'tags',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/tags')
                    ),
                  },
                  {
                    path: 'scheme',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/scheme')
                    ),
                  },
                  {
                    path: 'history',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/history')
                    ),
                  },
                  {
                    path: 'rewards',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/rewards')
                    ),
                  },
                  {
                    path: 'vouchers',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/vouchers')
                    ),
                  },
                  {
                    path: 'links',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/links')
                    ),
                  },
                  // The guest-facing microsite preview, moved off the Link
                  // Builder. Two routes, one component, differing only by which
                  // microsite page they frame.
                  {
                    path: 'preview-scheme',
                    component: <LoyaltySchemePreviewPage variant="sign-up" />,
                  },
                  {
                    path: 'preview-rewards',
                    component: <LoyaltySchemePreviewPage variant="rewards" />,
                  },
                  {
                    path: 'gift-cards',
                    component: lazy(
                      () => import('@/pages/[org_id]/loyalty/[id]/gift-cards')
                    ),
                  },
                  {
                    path: 'points-catchup',
                    capability: 'loyalty.points-catchup',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/venues/[serial]/loyalty/[loyalty_scheme_id]/points-catchup'
                        )
                    ),
                  },
                  {
                    path: 'member-imports',
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/loyalty/[id]/member-imports'
                            )
                        ),
                      },
                      {
                        path: ':member_import_id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/loyalty/[id]/member-imports/[member_import_id]'
                            )
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'gifting',
            component: lazy(() => import('@/pages/[org_id]/gifting/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/gifting')),
              },
              {
                path: 'scan',
                component: lazy(() => import('@/pages/[org_id]/gifting/scan')),
              },
              {
                path: 'activations',
                component: lazy(
                  () => import('@/pages/[org_id]/gifting/activations')
                ),
              },
              {
                path: 'reports',
                component: lazy(
                  () => import('@/pages/[org_id]/gifting/reports')
                ),
              },
              {
                path: 'cards',
                component: lazy(() => import('@/pages/[org_id]/gifting/cards')),
              },
              {
                path: ':id',
                component: lazy(
                  () => import('@/pages/[org_id]/gifting/[id]/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/gifting/[id]')
                    ),
                  },
                ],
              },
            ],
          },

          /**
           * Know your customer, under the Customers nav group.
           *
           * Deliberately NOT nested under `customer/`: `customer/:id` is the
           * guest profile route, so `customer/know-your-customer` would read
           * as a profile id. A sibling of `reports` instead, directly under
           * the org shell, which is where Full Bhuna's chrome comes from too.
           * The venue-scoped twin sits under `venues/:serial`.
           */
          {
            path: 'know-your-customer',
            capability: 'kyc.view',
            component: lazy(
              () => import('@/pages/[org_id]/know-your-customer')
            ),
          },

          /**
           * Reports
           */
          {
            path: 'reports',
            component: lazy(() => import('@/pages/[org_id]/reports/layout')),
            children: [
              {
                // Reports default to the Receipts (org overview) surface; the
                // nav sub-menu replaces the old card-grid landing.
                index: true,
                component: <Navigate to="receipts" replace />,
              },
              {
                path: 'receipts',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/receipts')
                ),
              },
              // Public alias for help-article links (product.stampede.ai/open/
              // reports/overview/…), so the Knowledge Base never prints the
              // internal route name. One alias per real full-bhuna section
              // below: an alias without a target would land on a 404.
              {
                path: 'overview',
                component: <Navigate to="../full-bhuna" replace />,
              },
              ...[
                'bookings',
                'revenue',
                'deposits',
                'marketing',
                'reviews',
                'wifi',
                'inbox',
                'enquiries',
                'loyalty',
              ].map((section) => ({
                path: `overview/${section}`,
                component: <Navigate to={`../full-bhuna/${section}`} replace />,
              })),
              {
                // Full Bhuna: every domain, backed by ClickHouse via trunk
                // GET /reporting/summary. Arbitrary date range and venue set;
                // day/week/month is a GROUP BY, not a rollup table.
                //
                // A route per section under a shared layout, so each section
                // is linkable and the back button works. The layout holds the
                // toolbar state and the single query, which is what keeps the
                // chosen dates and venues when moving between sections.
                path: 'full-bhuna',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/full-bhuna/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/reports/full-bhuna')
                    ),
                  },
                  {
                    path: 'bookings',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/bookings')
                    ),
                  },
                  {
                    path: 'revenue',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/revenue')
                    ),
                  },
                  {
                    path: 'deposits',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/deposits')
                    ),
                  },
                  {
                    path: 'marketing',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/marketing')
                    ),
                  },
                  {
                    path: 'reviews',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/reviews')
                    ),
                  },
                  {
                    path: 'wifi',
                    component: lazy(
                      () => import('@/pages/[org_id]/reports/full-bhuna/wifi')
                    ),
                  },
                  {
                    path: 'inbox',
                    component: lazy(
                      () => import('@/pages/[org_id]/reports/full-bhuna/inbox')
                    ),
                  },
                  {
                    path: 'enquiries',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/enquiries')
                    ),
                  },
                  {
                    path: 'loyalty',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/reports/full-bhuna/loyalty')
                    ),
                  },
                ],
              },
              {
                path: 'origin',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/origin')
                ),
              },
              {
                path: 'marketing-email',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/marketing/email')
                ),
              },
              {
                path: 'marketing-sms',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/marketing/sms')
                ),
              },
              {
                path: 'interactions',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/interactions')
                ),
              },
              {
                path: 'interactions/:id',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/interactions/[id]')
                ),
              },
              {
                path: 'customers',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/customers')
                ),
              },
              {
                path: 'transactions',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/transactions')
                ),
              },
              {
                path: 'my-reports',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/report-builder')
                ),
              },
              {
                path: 'monthly-retention',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/cohort/monthly')
                ),
              },
              {
                path: 'gifting',
                component: lazy(
                  () => import('@/pages/[org_id]/gifting/reports')
                ),
              },
              {
                path: 'tags',
                component: lazy(() => import('@/pages/[org_id]/reports/tags')),
              },
              {
                path: 'survey',
                component: lazy(
                  () => import('@/pages/[org_id]/reports/survey')
                ),
              },
            ],
          },

          /**
           * ALL OTHER ROUTES HERE
           */

          {
            path: 'marketplace',
            children: [
              {
                index: true,
                component: <Navigate to="apps" replace />,
              },
              {
                path: 'apps',
                component: lazy(
                  () => import('@/pages/[org_id]/marketplace/apps/layout')
                ),
                children: [
                  {
                    index: true,
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps')
                    ),
                  },
                  {
                    path: ':name',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/[name]')
                    ),
                  },

                  {
                    path: 'klaviyo',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/klaviyo')
                    ),
                  },
                  {
                    path: 'whatsapp',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/whatsapp')
                    ),
                  },
                  {
                    path: 'square',

                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import('@/pages/[org_id]/marketplace/apps/square')
                        ),
                      },
                      {
                        path: ':integration_id',
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketplace/apps/square/[integration_id]/page'
                                )
                            ),
                          },
                          {
                            path: 'setup',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/marketplace/apps/square/[integration_id]/setup'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'unifi',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/unifi')
                    ),
                  },
                  {
                    path: 'storekit',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/storekit')
                    ),
                  },
                  {
                    path: 'roller',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/roller')
                    ),
                  },
                  {
                    path: 'stripe',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/stripe')
                    ),
                  },
                  {
                    path: 'paypal',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/paypal')
                    ),
                  },

                  {
                    path: 'opentable',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/marketplace/apps/opentable')
                    ),
                  },
                  {
                    path: 'zapier',
                    component: lazy(
                      () => import('@/pages/[org_id]/marketplace/apps/zapier')
                    ),
                  },
                ],
              },
              {
                path: 'api-keys',
                component: lazy(
                  () => import('@/pages/[org_id]/marketplace/api-keys')
                ),
              },
              {
                path: 'api-keys/hooks/:hook_id',
                component: lazy(
                  () =>
                    import(
                      '@/pages/[org_id]/marketplace/api-keys/hooks/[hook_id]'
                    )
                ),
              },
            ],
          },
          {
            path: 'account',
            component: lazy(() => import('@/pages/[org_id]/account/layout')),
            children: [
              {
                index: true,
                component: lazy(() => import('@/pages/[org_id]/account')),
              },
              {
                path: 'profile',
                component: lazy(
                  () => import('@/pages/[org_id]/account/profile')
                ),
              },
              {
                path: 'notifications',
                component: lazy(
                  () => import('@/pages/[org_id]/account/notifications')
                ),
              },
              {
                path: 'organisation',
                // Admin-only, covering the whole subtree: billing, team,
                // settings, tags, brand kits, event log.
                //
                // Deliberately on THIS child rather than the `account` parent —
                // `account/profile` and `account/notifications` are the user's
                // own settings and every role needs them.
                capability: 'org.account',
                component: lazy(
                  () => import('@/pages/[org_id]/account/organisation/layout')
                ),
                children: [
                  {
                    index: true,
                    component: <div>Organisation</div>,
                  },
                  {
                    path: 'venues',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/account/organisation/venues')
                    ),
                  },
                  {
                    path: 'team',
                    component: lazy(
                      () => import('@/pages/[org_id]/account/organisation/team')
                    ),
                  },
                  {
                    path: 'groups',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/account/organisation/groups')
                    ),
                  },
                  {
                    path: 'settings',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/account/organisation/settings')
                    ),
                  },
                  {
                    path: 'tags',
                    component: lazy(
                      () => import('@/pages/[org_id]/account/organisation/tags')
                    ),
                  },
                  {
                    path: 'billing',
                    component: lazy(
                      () =>
                        import('@/pages/[org_id]/account/organisation/billing')
                    ),
                  },
                  {
                    path: 'billing-2',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/account/organisation/billing-2'
                        )
                    ),
                  },

                  {
                    path: 'event-log',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/account/organisation/event-log'
                        )
                    ),
                  },
                  {
                    path: 'brand',
                    component: lazy(
                      () =>
                        import(
                          '@/pages/[org_id]/account/organisation/brand/layout'
                        )
                    ),
                    children: [
                      {
                        index: true,
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/account/organisation/brand'
                            )
                        ),
                      },
                      {
                        path: ':id',
                        component: lazy(
                          () =>
                            import(
                              '@/pages/[org_id]/account/organisation/brand/[id]/layout'
                            )
                        ),
                        children: [
                          {
                            index: true,
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/account/organisation/brand/[id]'
                                )
                            ),
                          },
                          {
                            path: 'journey',
                            component: lazy(
                              () =>
                                import(
                                  '@/pages/[org_id]/account/organisation/brand/[id]/journey'
                                )
                            ),
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: '*',
            component: lazy(() => import('@/pages/error/404')),
          },
        ],
      },
      {
        path: '*',
        component: lazy(() => import('@/pages/error/404')),
      },
    ],
  },
  {
    path: '*',
    component: lazy(() => import('@/pages/error/404')),
  },
]
